/**
 * Lens Tunnel Manager - Moon Moon Agency
 * Drawer de personnalisation des verres pour Céline Roland Opticien.
 *
 * V1 (dry-run) : à la validation finale, on ne crée aucun produit Shopify et
 * on n'appelle pas /cart/add.js. On affiche dans le récap et on logue dans la
 * console le `optimum_ref` du verre choisi + détails gravure le cas échéant.
 *
 * Data : `theme/assets/tunnel-data.json` (généré par scripts/build-tunnel-asset.mjs).
 */

/*********************************************************************************************
 * 🔭 LENS TUNNEL MANAGER
 */
const LensTunnelManager = {
	config: {
		jsPdfCdn: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
		selectors: {
			trigger:    '[data-lens-tunnel-trigger]',
			wrapper:    '[data-lens-tunnel-wrapper]',
			drawer:     '[data-lens-tunnel-drawer]',
			close:      '[data-lens-tunnel-close]',
			filter:     '[data-lens-tunnel-filter]',
			back:       '[data-lens-tunnel-back]',
			progress:   '[data-lens-tunnel-progress]',
			progressBar:'[data-lens-tunnel-progress-bar]',
			content:    '[data-lens-tunnel-content]',
			title:      '[data-lens-tunnel-title]',
			footer:     '[data-lens-tunnel-footer]',
			nextBtn:    '[data-lens-tunnel-next]',
			footerTotal:'[data-lens-tunnel-footer-total]',
			liveRegion: '[data-lens-tunnel-live]',
		},
		classes: {
			open: 'mm-lt-is-open',
		},
		engravingMaxLength: 20,
		// Sous-steps fusionnés dans l'étape "combo" (catégorie + style + couleur + intensité).
		comboSubsteps: ['categorie', 'sous_categorie', 'couleur', 'cat_solaire'],
		// Opacité appliquée sur la preview du verre selon la cat solaire choisie.
		comboIntensityOpacity: { 'cat-1': 0.3, 'cat-2': 0.6, 'cat-3': 0.9 },
	},

	state: {
		ready: false,
		open: false,
		productHandle: null,
		productTitle: null,
		productPrice: null,
		montureVariantId: null,
		montureVariantTitle: null, // ex: "53/19" — null si "Default Title" ou produit sans variante
		montureIsPerce: null, // boolean | null (null = info indisponible, pas de filtrage)
		monturePrecommande: false, // variante en precommande -> property _precommande sur la ligne monture
		montureDisponibilite: '', // delivery_mode de la fiche -> property _disponibilite (espace client)
		montureLivraison: '', // livraison estimee de la fiche -> property _livraison_estimee (espace client)
		parcours: null,
		stepIndex: 0,
		steps: [],
		selections: {},
		engraving: null, // { color: 'transparent'|'gold'|'silver', zones: { lt|lb|rt|rb: {text, font} | null } }
		engravingPrice: 0, // [legacy] V2 utilise engravingPrices
		engravingPrices: { transparent: 50, gold: 55, silver: 55 },
		engravingSlogan: '',
		gravureEnabled: false,
		ordonnance: null, // { sphere_d, cylindre_d, axe_d, sphere_g, cylindre_g, axe_g, ecart_d, ecart_g, distance_p, addition, file, skipped }
		ordonnanceSkipEnabled: false,
		ordonnanceError: null,
		ordonnanceFieldErrors: new Set(), // Nom des champs ordonnance en erreur (set par _validateOrdonnance, consomme par _buildInput/_buildSelect pour afficher la border rouge)
		comboError: null,
		artDetail: null, // id du block art_detail_option sélectionné
		artDetailOptions: [], // peuplé depuis window.MM_LT_ART_DETAILS au init
	},

	data: null,
	dataPromise: null,

	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null,
	},

	/**
	 * Initialize the module
	 */
	init() {
		const wrapper = window.getElement(this.config.selectors.wrapper);
		if (!wrapper) return;

		// Cache DOM refs
		this.dom = {
			wrapper,
			drawer:      wrapper.querySelector(this.config.selectors.drawer),
			filter:      wrapper.querySelector(this.config.selectors.filter),
			closeBtn:    wrapper.querySelector(this.config.selectors.close),
			backBtn:     wrapper.querySelector(this.config.selectors.back),
			progress:    wrapper.querySelector(this.config.selectors.progress),
			progressBar: wrapper.querySelector(this.config.selectors.progressBar),
			content:     wrapper.querySelector(this.config.selectors.content),
			title:       wrapper.querySelector(this.config.selectors.title),
			footer:      wrapper.querySelector(this.config.selectors.footer),
			liveRegion:  wrapper.querySelector(this.config.selectors.liveRegion),
		};

		// Read schema config (price/options for gravure) from wrapper dataset
		this.state.engravingPrice = parseInt(wrapper.dataset.engravingPrice || '0', 10);
		this.state.engravingPrices = {
			transparent: parseInt(wrapper.dataset.engravingPriceTransparent || '50', 10),
			gold:        parseInt(wrapper.dataset.engravingPriceGold        || '55', 10),
			silver:      parseInt(wrapper.dataset.engravingPriceSilver      || '55', 10),
		};
		this.state.engravingSlogan = (wrapper.dataset.engravingSlogan || '').trim();
		this.state.artDetailOptions = Array.isArray(window.MM_LT_ART_DETAILS) ? window.MM_LT_ART_DETAILS : [];
		this.state.gravureEnabled = wrapper.dataset.gravureEnabled === 'true';
		this.state.ordonnanceSkipEnabled = wrapper.dataset.ordonnanceSkipEnabled === 'true';
		this.state.shippingText = (wrapper.dataset.shippingText || '').trim();
		this.state.ordSettings = {
			intermediateText:  (wrapper.dataset.ordonnanceIntermediateText || '').trim(),
			pupillaryHelp:     (wrapper.dataset.ordonnancePupillaryHelp || '').trim(),
			doubtText:         (wrapper.dataset.ordonnanceDoubtText || '').trim(),
			doubtText2:        (wrapper.dataset.ordonnanceDoubtText2 || '').trim(),
			phone:             (wrapper.dataset.ordonnancePhone || '').trim(),
			uploadTitle:       (wrapper.dataset.ordonnanceUploadSectionTitle || '').trim(),
			uploadSubtitle:    (wrapper.dataset.ordonnanceUploadSectionSubtitle || '').trim(),
			uploadFooter:      (wrapper.dataset.ordonnanceUploadSectionFooter || '').trim(),
		};
		this.state.comboSettings = {
			slogan:       (wrapper.dataset.comboSlogan || '').trim(),
			previewImage: (wrapper.dataset.comboPreviewImage || '').trim(),
			previewAlt:   (wrapper.dataset.comboPreviewAlt || '').trim(),
		};
		this.state.recapSettings = {
			title:            (wrapper.dataset.recapTitle || '').trim(),
			subtitle:         (wrapper.dataset.recapSubtitle || '').trim(),
			lensSectionLabel: (wrapper.dataset.recapLensSectionLabel || '').trim(),
			leadTime:         (wrapper.dataset.recapLeadTime || '').trim(),
			devisLabel:       (wrapper.dataset.recapDevisLabel || '').trim(),
			devisAction:      (wrapper.dataset.recapDevisAction || '').trim(),
			secondPairEnabled:  wrapper.dataset.recapSecondpairEnabled === 'true',
			secondPairTitle:    (wrapper.dataset.recapSecondpairTitle || '').trim(),
			secondPairMain:     (wrapper.dataset.recapSecondpairMain || '').trim(),
			secondPairMainSub:  (wrapper.dataset.recapSecondpairMainSub || '').trim(),
			secondPairSep:      (wrapper.dataset.recapSecondpairSeparator || '').trim(),
			secondPairAlt:      (wrapper.dataset.recapSecondpairAlt || '').trim(),
			secondPairAltSub:   (wrapper.dataset.recapSecondpairAltSub || '').trim(),
		};

		/* Optimum HDS bridge (GCF middleware) config + customer pre-fill.
		 * Si optimumGcfUrl est blank, les uploads sont skip (le tunnel fonctionne normalement). */
		this.state.optimum = {
			gcfUrl:        (wrapper.dataset.optimumGcfUrl || '').trim(),
			themeSecret:   (wrapper.dataset.optimumThemeSecret || '').trim(),
			clientId:      null, /* set apres 1er upload reussi */
			visiteId:      null, /* set apres 1er upload reussi */
			ordoUploaded:  false,
			devisUploaded: false,
		};
		this.state.identity = {
			email:     (wrapper.dataset.customerEmail || '').trim(),
			firstName: (wrapper.dataset.customerFirstname || '').trim(),
			lastName:  (wrapper.dataset.customerLastname || '').trim(),
		};

		/* FittingBox PD Measurement (mesure ecart pupillaire via webcam).
		 * Si apiKey blank, le bouton 'Mesurer maintenant' n'est pas rendu. */
		this.state.fittingboxPd = {
			apiKey:        (wrapper.dataset.fittingboxPdApiKey || '').trim(),
			scriptLoaded:  false,
			scriptLoading: null, /* Promise en cours pour partager les await concurrent */
		};

		/* Mentions obligatoires devis : N° FINESS + textes legaux richtext (CGV + footer societe).
		 * Les richtext arrivent en HTML escape, convertis en plain text au moment de la generation
		 * PDF via _richtextToPlainText (jsPDF n'a pas de parser HTML natif fiable).
		 * zoneAFrame* : produit Shopify configure pour la monture "100% sante" en Zone A du
		 * devis (typiquement une monture a 30 EUR remboursee integralement). Si non configure,
		 * fallback sur les valeurs par defaut (label generique + 30 EUR). */
		const zoneAFramePriceCents = parseInt(wrapper.dataset.quoteZoneAFramePrice || '0', 10) || 0;
		this.state.devisSettings = {
			finessNumber: (wrapper.dataset.finessNumber || '').trim(),
			legalText:    (wrapper.dataset.quoteLegalText  || '').trim(),
			footerText:   (wrapper.dataset.quoteFooterText || '').trim(),
			logoUrl:      (wrapper.dataset.quoteLogoUrl || '').trim(),
			companyInfo:  (wrapper.dataset.quoteCompanyInfo || '').trim(),
			zoneAFrameTitle:        (wrapper.dataset.quoteZoneAFrameTitle || '').trim(),
			zoneAFrameVariantTitle: (wrapper.dataset.quoteZoneAFrameVariantTitle || '').trim(),
			zoneAFramePriceCents,
			/* Charges async une fois (preload), reutilise pour chaque devis ensuite */
			logoBase64: null,
			logoAspectRatio: 1,
		};
		/* URL JSON LPP : lookup base remboursement SS + prix unitaire reglemente
		 * pour le devis. Lazy-loaded au 1er download de devis. */
		this.state.lensLppUrl = (wrapper.dataset.lensLppUrl || '').trim();
		this.state.lensLppTable = null;
		this._lensLppPromise = null;

		/* Preload logo en base64 en background — jsPDF.addImage a besoin d'une dataURL,
		 * et on doit connaitre le ratio pour calculer la hauteur a partir de la largeur fixe. */
		this._preloadDevisLogo();

		this._bindEvents();
		this.state.ready = true;
		window.log('info', '🔭 [LENS_TUNNEL]', 'Initialized');
	},

	_bindEvents() {
		// Trigger : ouverture depuis n'importe où sur la page
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.trigger);
			if (!trigger) return;
			e.preventDefault();
			this.open(trigger);
		});

		// Close & filter (delegation)
		this.dom.wrapper.addEventListener('click', (e) => {
			if (e.target.closest(this.config.selectors.close) ||
				e.target.closest(this.config.selectors.filter)) {
				this.close();
				return;
			}
			if (e.target.closest(this.config.selectors.back)) {
				this.goPrevious();
				return;
			}
			const optionBtn = e.target.closest('[data-lens-option]');
			if (optionBtn && !optionBtn.disabled) {
				this._handleOptionClick(optionBtn);
				return;
			}
			// Gravure : zone toggle (+/coche sur la preview)
			const zoneBtn = e.target.closest('[data-lens-eng-zone]');
			if (zoneBtn) {
				this._toggleEngravingZone(zoneBtn.dataset.lensEngZone);
				return;
			}
			// Gravure : retirer une zone via le bouton "Retirer" de la card
			const zoneRemoveBtn = e.target.closest('[data-lens-eng-zone-remove]');
			if (zoneRemoveBtn) {
				this._removeEngravingZone(zoneRemoveBtn.dataset.lensEngZoneRemove);
				return;
			}
			// Gravure : effacer le texte (croix en mode erreur)
			const zoneClearBtn = e.target.closest('[data-lens-eng-zone-clear]');
			if (zoneClearBtn) {
				this._clearEngravingZoneText(zoneClearBtn.dataset.lensEngZoneClear);
				return;
			}
			// Gravure : changement de couleur globale
			const colorBtn = e.target.closest('[data-lens-eng-color]');
			if (colorBtn) {
				this._setEngravingColor(colorBtn.dataset.lensEngColor);
				return;
			}
			// Gravure : changement de police pour une zone
			const fontBtn = e.target.closest('[data-lens-eng-font]');
			if (fontBtn) {
				this._setEngravingZoneFont(fontBtn.dataset.lensEngZoneTarget, fontBtn.dataset.lensEngFont);
				return;
			}
			if (e.target.closest('[data-lens-tunnel-next]')) {
				this.goNext();
				return;
			}
			if (e.target.closest('[data-lens-tunnel-validate]')) {
				this._validate();
				return;
			}
			if (e.target.closest('[data-lens-tunnel-quote]')) {
				this._downloadQuote(e.target.closest('[data-lens-tunnel-quote]'));
				return;
			}
			if (e.target.closest('[data-lens-tunnel-restart]')) {
				this._restart();
				return;
			}
			// Ordonnance : tooltip aide
			if (e.target.closest('[data-lens-ord-help-toggle]')) {
				this._toggleOrdonnanceHelp();
				return;
			}
			// Ordonnance : skip (mode test)
			if (e.target.closest('[data-lens-ord-skip]')) {
				this._handleOrdonnanceSkip();
				return;
			}
			// Ordonnance : retirer fichier joint
			if (e.target.closest('[data-lens-ord-file-remove]')) {
				this._handleOrdonnanceFileRemove();
				return;
			}
			// Ordonnance : mesure ecart pupillaire via FittingBox
			if (e.target.closest('[data-lens-ord-fb-measure]')) {
				e.preventDefault();
				this._launchFittingboxPd();
				return;
			}
		});

		// Inputs live update (engraving zones + ordonnance text fields)
		this.dom.wrapper.addEventListener('input', (e) => {
			if (e.target.matches('[data-lens-eng-zone-text]')) {
				this._updateEngravingZoneText(e.target.dataset.lensEngZoneText, e.target.value);
				return;
			}
			if (e.target.matches('[data-lens-ord-field]')) {
				const fieldName = e.target.dataset.lensOrdField;
				this._updateOrdonnanceField(fieldName, e.target.value);
				/* Reset live du bandeau d'erreur formulaire dès que l'utilisateur tape (sans re-render). */
				if (this.state.ordonnanceError) {
					this.state.ordonnanceError = null;
					const errEl = this.dom.wrapper.querySelector('.mm-lt-ordonnance-error');
					if (errEl) errEl.remove();
				}
				/* Reset visuel de la border rouge sur le champ corrige, sans re-render. */
				if (this.state.ordonnanceFieldErrors?.has(fieldName)) {
					this.state.ordonnanceFieldErrors.delete(fieldName);
					e.target.classList.remove('mm-lt-ord-input--error');
					e.target.removeAttribute('aria-invalid');
				}
				return;
			}
			if (e.target.matches('[data-lens-ord-email]')) {
				/* Update silencieux : pas de re-render (l'input garde le focus) */
				this._ensureOrdonnance();
				this.state.ordonnance.email = e.target.value;
				this.state.identity.email = e.target.value;
				if (this.state.ordonnanceEmailError) {
					this.state.ordonnanceEmailError = null;
					/* Sync visuel de l'erreur sans re-render complet */
					const errEl = this.dom.wrapper.querySelector('.mm-lt-ord-email-error');
					if (errEl) errEl.remove();
					e.target.classList.remove('mm-lt-ord-email-input--error');
					e.target.removeAttribute('aria-invalid');
				}
				return;
			}
		});

		// Ordonnance : upload fichier (change event sur input file)
		this.dom.wrapper.addEventListener('change', (e) => {
			if (e.target.matches('[data-lens-ord-file]')) {
				this._handleOrdonnanceFile(e.target);
			}
		});

		// Fermer si le cart drawer s'ouvre (exclusion mutuelle)
		document.addEventListener('cart:opened', () => {
			if (this.state.open) this.close();
		});
	},

	/**
	 * Lazy-load le JSON des combinaisons.
	 */
	async _loadData() {
		if (this.data) return this.data;
		if (this.dataPromise) return this.dataPromise;

		const url = this.dom.wrapper.dataset.tunnelDataUrl;
		if (!url) {
			window.log('error', '🔭 [LENS_TUNNEL]', 'Missing data-tunnel-data-url on wrapper');
			return null;
		}

		this.dataPromise = fetch(url, { credentials: 'omit' })
			.then(r => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.json();
			})
			.then(json => {
				this.data = json;
				window.log('info', '🔭 [LENS_TUNNEL]', `Data loaded (${json.combinations.length} combinations)`);
				return json;
			})
			.catch(err => {
				window.log('error', '🔭 [LENS_TUNNEL]', 'Failed to load tunnel data', { error: err.message });
				this.dataPromise = null;
				return null;
			});

		return this.dataPromise;
	},

	/**
	 * Open drawer for a given product (monture).
	 */
	async open(triggerElement) {
		if (!this.state.ready) return;

		// Reset state for this opening
		this._resetState();
		this.state.productHandle = triggerElement.dataset.productHandle || null;
		this.state.productTitle = triggerElement.dataset.productTitle || null;
		this.state.productPrice = parseInt(triggerElement.dataset.productPrice || '0', 10);
		this.state.montureVariantId = triggerElement.dataset.montureVariantId || null;
		this.state.monturePrecommande = triggerElement.dataset.monturePrecommande === 'true';
		this.state.montureDisponibilite = triggerElement.dataset.montureDisponibilite || '';
		this.state.montureLivraison = triggerElement.dataset.montureLivraison || '';
		/* Titre de la variante (couleur / taille). On filtre "Default Title" + on nettoie
		 * la partie couleur qui contient souvent un suffixe SKU (ex: "Or-RB3548N 001 / 54-21"
		 * → "Or / 54-21"). La taille ("54-21") reste intacte car purement numérique. */
		const variantTitle = triggerElement.dataset.montureVariantTitle || '';
		this.state.montureVariantTitle = (variantTitle && variantTitle !== 'Default Title')
			? this._cleanVariantTitle(variantTitle)
			: null;
		// Monture percée ? Lu depuis le metafield product.metafields.custom.perce
		// → contraint les épaisseurs disponibles dans le tunnel
		// trim() defensif : le snippet Liquid mm-monture-perce peut injecter un
		// newline suivant les cas (whitespace autour du {% doc %} etc.), et
		// "\ntrue" === "true" retourne false -> filter perce jamais applique.
		const perceAttr = (triggerElement.dataset.monturePerce || '').trim();
		this.state.montureIsPerce = perceAttr === 'true' ? true
			: perceAttr === 'false' ? false
			: null;

		// Show drawer immediately, render loading placeholder
		this.dom.wrapper.classList.add(this.config.classes.open);
		document.documentElement.classList.add('stop-scrolling');
		document.querySelectorAll('main, header, footer').forEach(el => {
			if (!this.dom.wrapper.contains(el)) el.inert = true;
		});

		this.state.open = true;
		this._renderLoading();
		this.setupFocusTrap(this.dom.wrapper, triggerElement);

		window.emit('global:lens-tunnel-opened', { handle: this.state.productHandle });

		// Lazy-load data
		const data = await this._loadData();
		if (!data) {
			this._renderError(window.MM_LT_I18N?.errors_data_load || 'Erreur de chargement');
			return;
		}

		// Démarre toujours par l'étape "correction" (choix du parcours)
		this.state.steps = ['correction'];
		this.state.stepIndex = 0;
		this._render();
	},

	close() {
		if (!this.state.open) return;
		this.dom.wrapper.classList.remove(this.config.classes.open);
		document.documentElement.classList.remove('stop-scrolling');
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);
		this.removeFocusTrap();
		this.state.open = false;
		window.emit('global:lens-tunnel-closed');
	},

	_resetState() {
		this.state.selections = {};
		this.state.engraving = null;
		this.state.parcours = null;
		this.state.stepIndex = 0;
		this.state.steps = [];
		this.state.montureIsPerce = null;
		this.state.ordonnance = null;
		this.state.ordonnanceError = null;
		this.state.comboError = null;
		this.state.artDetail = null;
	},

	/* ====================================================================== */
	/* STEP NAVIGATION                                                        */
	/* ====================================================================== */

	_handleOptionClick(btn) {
		const stepKey = btn.dataset.stepKey;
		const optionId = btn.dataset.optionId;
		if (!stepKey || !optionId) return;

		if (stepKey === 'correction') {
			// Choix du parcours. L'étape "ordonnance" n'est insérée que pour les parcours
			// avec correction (Sans Correction n'a pas besoin d'ordonnance).
			// Les 4 sous-steps catégorie/sous_categorie/couleur/cat_solaire sont fusionnés
			// en une seule étape "combo" (étape visuelle unifiée).
			this.state.parcours = optionId;
			const p = this.data.parcours.find(x => x.id === optionId);
			const needsOrdonnance = optionId !== 'sans-correction';
			const collapsedSteps = this._collapseToCombo(p.steps);
			const hasArtDetail = this.state.artDetailOptions && this.state.artDetailOptions.length > 0;
			this.state.steps = [
				'correction',
				...(needsOrdonnance ? ['ordonnance'] : []),
				...collapsedSteps,
				...(hasArtDetail ? ['art_detail'] : []),
				'gravure',
				'recap',
			];
			this._announce(`Parcours : ${this._label(p)}`);
			this.goNext();
			return;
		}

		// Si on est dans l'étape combo : update la sélection du sous-step sans avancer.
		// Cascade : changer catégorie reset sous_categorie/couleur/cat_solaire, etc.
		const currentStep = this.state.steps[this.state.stepIndex];
		if (currentStep === 'combo' && this.config.comboSubsteps.includes(stepKey)) {
			this.state.selections[stepKey] = optionId;
			this._cascadeResetCombo(stepKey);
			this._announce(`${stepKey} : ${btn.dataset.optionLabel || optionId}`);
			this._render();
			return;
		}

		// Étape art_detail : sélection d'une option art du détail (bloc).
		// Le routing se fait via _handleArtDetailClick (selon enables_engraving).
		if (currentStep === 'art_detail' && stepKey === 'art_detail') {
			this._handleArtDetailClick(optionId);
			return;
		}

		// Sélection standard (étape options simples qui auto-advance)
		this.state.selections[stepKey] = optionId;
		// Réinitialiser les sélections aux étapes ultérieures (peuvent devenir incompatibles)
		const idx = this.state.steps.indexOf(stepKey);
		for (let i = idx + 1; i < this.state.steps.length; i++) {
			const futureKey = this.state.steps[i];
			if (['correction', 'art_detail', 'gravure', 'recap', 'combo'].includes(futureKey)) continue;
			delete this.state.selections[futureKey];
		}
		this._announce(`${stepKey} sélectionné : ${btn.dataset.optionLabel || optionId}`);
		this.goNext();
	},

	/**
	 * Click sur une option "Art du détail" :
	 * - Si l'option mène à la gravure (`enables_engraving=true`) → goNext vers la step gravure.
	 * - Sinon → on saute la step gravure et on va direct au récap (state.engraving reset à null).
	 */
	_handleArtDetailClick(artId) {
		const opt = (this.state.artDetailOptions || []).find(o => o.id === artId);
		if (!opt) return;
		// Option grisée (perce_only sur monture non-percée OU not_perce sur monture percée) → ignorer le click
		if (opt.perce_only && this.state.montureIsPerce !== true) return;
		if (opt.not_perce  && this.state.montureIsPerce === true) return;
		this.state.artDetail = artId;
		this._announce(`Art du détail : ${opt.label}`);
		// Si l'option ne mène pas à la gravure, on s'assure que state.engraving est vide
		// pour ne rien faire apparaître au récap, et on saute la step gravure.
		if (!opt.enables_engraving) {
			this.state.engraving = null;
		}
		this.goNext();
	},

	/**
	 * Remplace la séquence [categorie, sous_categorie, couleur, cat_solaire] par 'combo'.
	 * Les autres steps (type_verre, taille_verre, anti_reflet) sont conservés.
	 */
	_collapseToCombo(steps) {
		const subs = this.config.comboSubsteps;
		const out = [];
		let comboInserted = false;
		for (const s of steps) {
			if (subs.includes(s)) {
				if (!comboInserted) {
					out.push('combo');
					comboInserted = true;
				}
			} else {
				out.push(s);
			}
		}
		return out;
	},

	/**
	 * Quand un sous-step combo change, on reset les sous-steps en aval pour éviter
	 * les sélections devenues incompatibles (cascade catégorie → style → couleur → intensité).
	 */
	_cascadeResetCombo(changedSubstep) {
		const subs = this.config.comboSubsteps;
		const idx = subs.indexOf(changedSubstep);
		if (idx < 0) return;
		for (let i = idx + 1; i < subs.length; i++) {
			delete this.state.selections[subs[i]];
		}
	},

	goNext() {
		// Si on est à l'étape ordonnance, valider avant de passer
		const currentKey = this.state.steps[this.state.stepIndex];
		if (currentKey === 'ordonnance' && !this._validateOrdonnance()) {
			this._render();
			/* Le message d'erreur est en haut du panel : si l'utilisateur a scrollé
			 * en bas (écart pupillaire, distance...), il ne le verrait pas. */
			if (this.dom.content) this.dom.content.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}
		// Si on est à l'étape combo, valider que toutes les dimensions visibles sont remplies
		if (currentKey === 'combo' && !this._validateCombo()) {
			this._render();
			return;
		}
		// Si on est à l'étape gravure : bloquer si une zone activée est invalide.
		// Si aucune zone activée → on autorise (l'utilisateur saute la gravure).
		if (currentKey === 'gravure' && this._hasInvalidEngravingZone()) {
			return; // le footer affiche déjà le CTA disabled, rien à faire
		}
		// Avance d'une étape, en sautant les étapes "automatiques"
		// (= étapes où il n'y a qu'une valeur possible ou que des null)
		let next = this.state.stepIndex + 1;
		while (next < this.state.steps.length) {
			const key = this.state.steps[next];
			if (this._isAutoSkippable(key)) {
				// Auto-select si une seule valeur, sinon skip silencieusement (null partout)
				const opts = this._getOptionsForStep(key);
				if (opts.length === 1) {
					this.state.selections[key] = opts[0].id;
				}
				next++;
			} else {
				break;
			}
		}
		this.state.stepIndex = next;
		if (this.state.stepIndex >= this.state.steps.length) {
			this.state.stepIndex = this.state.steps.length - 1;
		}
		this._render();
	},

	goPrevious() {
		let prev = this.state.stepIndex - 1;
		while (prev > 0) {
			const key = this.state.steps[prev];
			/* En remontant, on n'auto-skip QUE les steps structurellement désactivés
			 * (ex: art_detail sans blocks). On ne saute PAS un step parce qu'il n'a
			 * qu'une seule option compatible avec les sélections aval — sinon l'user
			 * reste bloqué et ne peut plus changer d'avis (ex: passer transparents → solaires). */
			if (this._isStepStructurallyDisabled(key)) {
				delete this.state.selections[key];
				prev--;
			} else {
				break;
			}
		}
		this.state.stepIndex = Math.max(0, prev);
		this._render();
	},

	/**
	 * Vrai uniquement pour les steps qui n'ont aucun sens dans la config courante,
	 * indépendamment des sélections aval. Utilisé par goPrevious pour ne pas
	 * piéger l'utilisateur qui voudrait remonter changer un choix structurant.
	 */
	_isStepStructurallyDisabled(key) {
		if (key === 'art_detail') {
			return !this.state.artDetailOptions
				|| this.state.artDetailOptions.length === 0
				|| !this.state.gravureEnabled;
		}
		if (key === 'gravure') {
			if (this.state.artDetail) {
				const opt = (this.state.artDetailOptions || []).find(o => o.id === this.state.artDetail);
				if (opt && !opt.enables_engraving) return true;
			}
			return false;
		}
		return false;
	},

	_isAutoSkippable(key) {
		// art_detail : skip si pas de blocks configurés ou si gravure désactivée globalement
		if (key === 'art_detail') {
			return !this.state.artDetailOptions || this.state.artDetailOptions.length === 0 || !this.state.gravureEnabled;
		}
		// gravure : skip si l'option art_detail sélectionnée ne mène pas à la gravure
		if (key === 'gravure') {
			if (this.state.artDetail) {
				const opt = (this.state.artDetailOptions || []).find(o => o.id === this.state.artDetail);
				if (opt && !opt.enables_engraving) return true;
			}
			// Sinon comportement par défaut (non skippable)
			return false;
		}
		if (['correction', 'ordonnance', 'recap'].includes(key)) return false;
		// taille_verre : étape importante (impact prix + perçage), on l'affiche
		// toujours même s'il ne reste qu'une seule option valide après filtrage
		// (ex: monture percée + transparents → uniquement "Résistant 1,6").
		if (key === 'taille_verre') {
			return this._getOptionsForStep(key).length === 0;
		}
		if (key === 'combo') {
			// Combo skip uniquement si AUCUNE des 4 sous-dimensions n'a d'options valides
			// (cas Sans Correction Transparent : type_verre=transparents → ni catégorie ni style ni couleur ni intensité).
			return this.config.comboSubsteps.every(sub => this._getOptionsForStep(sub).length === 0);
		}
		const opts = this._getOptionsForStep(key);
		return opts.length <= 1;
	},

	/* ====================================================================== */
	/* DATA QUERIES                                                            */
	/* ====================================================================== */

	/**
	 * Filtre les combinaisons compatibles avec le parcours + les sélections courantes.
	 * @param {string[]} excludeKeys - clés de selections à ignorer dans le filtre. Utile
	 *   pour `_getOptionsForStep` qui doit lister TOUTES les options d'une dimension,
	 *   indépendamment du choix déjà fait pour cette dimension (cas du combo où l'on
	 *   reste sur la même étape après une sélection — sinon on ne verrait plus qu'une
	 *   seule option par dimension : celle déjà sélectionnée).
	 */
	_getCompatibleCombinations(excludeKeys = []) {
		if (!this.data || !this.state.parcours) return [];
		return this.data.combinations.filter(c => {
			if (c.parcours !== this.state.parcours) return false;
			for (const [key, val] of Object.entries(this.state.selections)) {
				if (excludeKeys.includes(key)) continue;
				if (!c[key] || c[key].id !== val) return false;
			}
			return true;
		});
	},

	/**
	 * Retourne les options uniques disponibles pour une étape donnée, en filtrant
	 * par les sélections des AUTRES dimensions. Chaque option est annotée `hasValid`
	 * (au moins une combo terminale valide existe), `hasExcluded`, `hasMissingOptimum`.
	 */
	_getOptionsForStep(stepKey) {
		const compatible = this._getCompatibleCombinations([stepKey]);
		const map = new Map();
		for (const c of compatible) {
			const opt = c[stepKey];
			if (!opt) continue;
			if (!map.has(opt.id)) {
				map.set(opt.id, {
					id: opt.id,
					label: opt.label,
					price: opt.price || 0,
					perce_only: opt.perce_only === true,
					not_perce:  opt.not_perce  === true,
					hasValid: false,
					hasExcluded: false,
					hasMissingOptimum: false,
				});
			}
			const entry = map.get(opt.id);
			if (c.exclu) entry.hasExcluded = true;
			else if (!c.optimum_ref) entry.hasMissingOptimum = true;
			else entry.hasValid = true;
		}
		return this._filterByPerceRule([...map.values()]);
	},

	/**
	 * Applique les règles de perçage selon les flags des options :
	 * - perce_only=true  : option RESERVEE aux montures percées (tag CSV `[perce]`)
	 * - not_perce=true   : option EXCLUE des montures percées (tag CSV `[pas perce]`)
	 *
	 * Logique :
	 * - Monture percée  + au moins 1 perce_only dans la liste → on ne garde QUE les perce_only
	 *                   + en plus on retire les not_perce
	 * - Monture cerclée + on EXCLUT les perce_only (peu importe s'il y en a)
	 *                   + on garde les not_perce (compatibles avec non-percées)
	 * - Pas d'info perçage → tout autorisé
	 */
	_filterByPerceRule(options) {
		const isPerce = this.state.montureIsPerce;
		if (isPerce === null || isPerce === undefined) return options;
		let filtered = options;
		if (isPerce) {
			const hasPerceOnly = filtered.some(o => o.perce_only);
			if (hasPerceOnly) filtered = filtered.filter(o => o.perce_only);
			/* Exclut les options [pas perce] sur montures percées */
			filtered = filtered.filter(o => !o.not_perce);
		} else {
			/* Montures cerclées : exclut les [perce] */
			filtered = filtered.filter(o => !o.perce_only);
		}
		return filtered;
	},

	_getFinalCombination() {
		const compatible = this._getCompatibleCombinations();
		return compatible.find(c => !c.exclu && c.optimum_ref) || null;
	},

	_getTotalExtra() {
		/* Somme des prix des options EXPLICITEMENT sélectionnées par l'utilisateur (ou
		 * auto-sélectionnées par goNext quand une seule option existait). N'utilise plus
		 * _getFinalCombination (qui spéculait sur la 1re combo compatible et incluait
		 * des prix d'options pas encore choisies). */
		let lensTotal = 0;
		const priceFields = [
			'type_verre', 'taille_verre', 'gamme_prog',
			'categorie', 'sous_categorie', 'couleur', 'cat_solaire',
			'anti_reflet',
		];
		for (const key of priceFields) {
			const selId = this.state.selections[key];
			if (!selId) continue;
			const opts = this._getOptionsForStep(key);
			const opt = opts.find(o => o.id === selId);
			if (opt && typeof opt.price === 'number') {
				lensTotal += opt.price;
			}
		}
		return lensTotal + this._getEngravingTotal() + this._getArtDetailFlatTotal();
	},

	/* Codes de regroupement par zone du devis (cf. CSV "Arborescence Pole Optique").
	 * Retourne { zoneA, zoneB }.
	 *
	 * Zone B (verres CLASSE B, premium) : VU1-VU7 unifocal / VM1-VM7 progressif
	 *   - pas de cyl    : |SPH| <= 6 -> *1, sinon *4
	 *   - SPH+, CYL > 0 : S = SPH + CYL/2 ; S <= 6 -> *3, sinon *7
	 *   - SPH-, CYL > 0 : |SPH| > 6 -> *6, CYL > 4 -> *5, sinon *2
	 *
	 * Zone A (verres CLASSE A, offre 100% Sante) : V01/V02 unifocal, V03/V04 progressif
	 *   Unifocal : seuil |SPH| > 6 (ou S > 6 pour SPH+ avec cyl) ou cyl > 4 -> V02, sinon V01
	 *   Progressif : seuils differents selon presence cyl
	 *     - sans cyl     : |SPH| > 4 -> V04, sinon V03
	 *     - SPH+, cyl>0  : S > 8 -> V04, sinon V03  (S = SPH + cyl/2)
	 *     - SPH-, cyl>0  : |SPH| > 8 -> V04, sinon V03
	 *     - cyl > 4      : toujours V04
	 *
	 * sphereStr/cylindreStr : chaines FR ("-2,50" / "+1,75") ou vides (ordo non saisie). */
	_getLensRegroupement(isProgressif, sphereStr, cylindreStr) {
		const empty = { zoneA: '—', zoneB: '—' };
		if (sphereStr == null || sphereStr === '') return empty;
		const sphere = parseFloat(String(sphereStr).replace(',', '.'));
		if (isNaN(sphere)) return empty;
		const cylindre = Math.abs(parseFloat(String(cylindreStr || '0').replace(',', '.'))) || 0;
		const absSphere = Math.abs(sphere);

		/* Zone B */
		const prefixB = isProgressif ? 'VM' : 'VU';
		let zoneB;
		if (cylindre === 0) {
			zoneB = absSphere <= 6 ? `${prefixB}1` : `${prefixB}4`;
		} else if (sphere > 0) {
			const s = sphere + cylindre / 2;
			zoneB = s <= 6 ? `${prefixB}3` : `${prefixB}7`;
		} else if (absSphere > 6) {
			zoneB = `${prefixB}6`;
		} else if (cylindre > 4) {
			zoneB = `${prefixB}5`;
		} else {
			zoneB = `${prefixB}2`;
		}

		/* Zone A — seuils differents pour progressif */
		let zoneA;
		if (isProgressif) {
			if (cylindre > 4) {
				zoneA = 'V04';
			} else if (cylindre > 0) {
				if (sphere > 0) {
					const s = sphere + cylindre / 2;
					zoneA = s > 8 ? 'V04' : 'V03';
				} else {
					zoneA = absSphere > 8 ? 'V04' : 'V03';
				}
			} else {
				zoneA = absSphere > 4 ? 'V04' : 'V03';
			}
		} else {
			if (cylindre > 4) {
				zoneA = 'V02';
			} else if (cylindre > 0 && sphere > 0) {
				const s = sphere + cylindre / 2;
				zoneA = s > 6 ? 'V02' : 'V01';
			} else {
				zoneA = absSphere > 6 ? 'V02' : 'V01';
			}
		}

		return { zoneA, zoneB };
	},

	/* Code de regroupement monture LPP 2026 (V1 : tout traite comme adulte).
	 * - Adulte ≤ 30 EUR  : M01
	 * - Adulte > 30 EUR  : M03
	 * (Future : enfant ≤ 30 -> M02, enfant > 30 -> M04 — necessitera une detection
	 * via metafield ou tag produit ; non implemente en V1, cf. brief client.)
	 * priceEur : prix de la monture en euros (number). */
	_getFrameRegroupement(priceEur) {
		return priceEur > 30 ? 'M03' : 'M01';
	},

	/* True si l'utilisateur a selectionne une teinte solaire (option cat_solaire).
	 * Si oui, on ajoute une ligne "OPTIQUE, SUPPLEMENT POUR VERRES TEINTES" (SV4) au
	 * devis dans les 2 zones — sans prix ni remboursement (option intrinseque du verre). */
	_isSolaire() {
		return !!this.state.selections?.cat_solaire;
	},

	/**
	 * Prix forfaitaire de l'art_detail sélectionné quand il NE mène PAS à la gravure
	 * (ex: Diamond cut, Insertion diamant → +350€ fixe par paire).
	 * Si enables_engraving = true, le prix est déjà comptabilisé par _getEngravingTotal
	 * (× nb_zones), on ne le compte donc PAS ici pour éviter le double comptage.
	 */
	_getArtDetailFlatTotal() {
		const artOpt = this.state.artDetail
			? (this.state.artDetailOptions || []).find(o => o.id === this.state.artDetail)
			: null;
		if (!artOpt || typeof artOpt.price_eur !== 'number' || artOpt.price_eur <= 0) return 0;
		if (artOpt.enables_engraving) return 0;
		return artOpt.price_eur;
	},

	/**
	 * Prix de gravure :
	 * - V2 : `art_detail.price_eur × nb_zones_activées` (le prix d'UNE gravure est défini
	 *   sur le block art_detail sélectionné). Comptage basé sur les zones ACTIVÉES (cochées),
	 *   pas sur le texte saisi : le prix s'affiche dès qu'une zone est cliquée.
	 * - Fallback legacy : `engravingPrices[color] × nb_verres_actifs` (si pas d'art_detail).
	 */
	_getEngravingTotal() {
		const activeZones = this._getActiveZoneCount();
		if (activeZones === 0) return 0;
		const artOpt = this.state.artDetail
			? (this.state.artDetailOptions || []).find(o => o.id === this.state.artDetail)
			: null;
		const eng = this.state.engraving;
		const color = eng?.color || 'transparent';

		/* V2 : si art_detail mène à la gravure (Gravure Transparente / Dorée / Argentée),
		 * utiliser engravingPrices[color] (3 prix par couleur) × activeZones.
		 * Le price_eur du block sert de fallback si la couleur n'a pas de prix configuré. */
		if (artOpt && artOpt.enables_engraving) {
			const colorPrice = this.state.engravingPrices[color] || artOpt.price_eur || 0;
			return colorPrice * activeZones;
		}

		/* art_detail avec price_eur fixe SANS gravure : ne devrait pas arriver ici
		 * (activeZones serait 0). Conservé par sécurité. */
		if (artOpt && typeof artOpt.price_eur === 'number' && artOpt.price_eur > 0) {
			return artOpt.price_eur * activeZones;
		}

		/* Fallback legacy (pas d'art_detail configuré) */
		const colorPrice = this.state.engravingPrices[color] || 0;
		return colorPrice * activeZones;
	},

	/**
	 * Label affichable pour un art_detail qui mène à la gravure :
	 * remplace le label statique du block (ex: "Gravure Transparente") par un label
	 * dynamique qui reflète la couleur choisie dans le step gravure.
	 * Format : "{title_v2} {colorLabel}" (ex: "Gravure Dorée").
	 */
	_getEngravingDisplayLabel(artOpt) {
		if (!artOpt) return '';
		if (!artOpt.enables_engraving) return artOpt.label || '';
		const color = this.state.engraving?.color || 'transparent';
		const colorLabel = this._t(`engraving.color_${color}`, color);
		const baseLabel = this._t('engraving.title_v2', 'Gravure');
		return `${baseLabel} ${colorLabel}`;
	},

	_getActiveZoneCount() {
		const eng = this.state.engraving;
		if (!eng) return 0;
		return ['lt','lb','rt','rb'].filter(k => eng.zones[k] !== null).length;
	},

	_getActiveLensCount() {
		const eng = this.state.engraving;
		if (!eng) return 0;
		return ((eng.zones.lt !== null || eng.zones.lb !== null) ? 1 : 0)
		     + ((eng.zones.rt !== null || eng.zones.rb !== null) ? 1 : 0);
	},

	/* Legacy : compte les VERRES avec au moins une zone gravée VALIDE (texte non-vide). */
	_getEngravedLensCount() {
		const eng = this.state.engraving;
		if (!eng) return 0;
		const leftEngraved  = this._isZoneEngraved(eng.zones.lt) || this._isZoneEngraved(eng.zones.lb);
		const rightEngraved = this._isZoneEngraved(eng.zones.rt) || this._isZoneEngraved(eng.zones.rb);
		return (leftEngraved ? 1 : 0) + (rightEngraved ? 1 : 0);
	},

	_isZoneEngraved(zone) {
		return !!(zone && zone.text && zone.text.trim().length > 0 && zone.text.length <= this.config.engravingMaxLength);
	},

	_hasAnyEngravingZone() {
		const eng = this.state.engraving;
		if (!eng) return false;
		return ['lt','lb','rt','rb'].some(k => this._isZoneEngraved(eng.zones[k]));
	},

	_hasInvalidEngravingZone() {
		const eng = this.state.engraving;
		if (!eng) return false;
		return ['lt','lb','rt','rb'].some(k => {
			const z = eng.zones[k];
			if (!z) return false; // zone non activée → pas d'erreur
			// Zone activée mais texte vide OU trop long → erreur
			return !z.text || !z.text.trim() || z.text.length > this.config.engravingMaxLength;
		});
	},

	/* ====================================================================== */
	/* ART DETAIL (étape précédant la gravure)                                 */
	/* ====================================================================== */

	_renderArtDetail() {
		const options = this.state.artDetailOptions || [];
		const isPerce = this.state.montureIsPerce === true;
		const items = options.map(opt => this._buildArtDetailCard(opt, isPerce)).join('');
		this.dom.content.innerHTML = `
			<div class="mm-lt-step-header">
				<h2 class="text-l mm-centered">${this._escape(this._t('art_detail.title', 'Art du détail'))}</h2>
			</div>
			<div class="mm-lt-options">${items}</div>
		`;
	},

	_buildArtDetailCard(opt, isPerce) {
		const isSelected = this.state.artDetail === opt.id;
		const isDisabled = opt.perce_only && !isPerce;
		const cls = [
			'mm-lt-option',
			'mm-lt-option--rich',
			isSelected ? 'mm-lt-option--selected' : '',
			isDisabled ? 'mm-lt-option--disabled' : '',
		].filter(Boolean).join(' ');

		// Prix : si price_eur > 0 → "+XX€" (ou "à partir de +XX€" pour les options qui
		// mènent à la perso). Si price_eur = 0 ou vide → fallback texte (si renseigné),
		// sinon rien (pas d'affichage "+0€" disgracieux).
		let priceText = '';
		const price = Number(opt.price_eur);
		if (Number.isFinite(price) && price > 0) {
			const prefix = opt.enables_engraving ? this._t('art_detail.price_prefix', 'à partir de ') : '';
			priceText = `${prefix}+${price}€`;
		} else if (opt.price_text_fallback && String(opt.price_text_fallback).trim()) {
			priceText = opt.price_text_fallback;
		}

		const image = opt.image
			? `<img src="${this._escape(opt.image)}" alt="${this._escape(opt.image_alt || this._label(opt))}" loading="lazy">`
			: '';

		const restricted = isDisabled && opt.restricted_message
			? `<p class="mm-lt-option-hint text-sm mm-black-500">${this._escape(opt.restricted_message)}</p>`
			: '';

		return `
			<button type="button"
				class="${cls}"
				data-lens-option
				data-step-key="art_detail"
				data-option-id="${this._escape(opt.id)}"
				data-option-label="${this._escape(opt.label)}"
				aria-pressed="${isSelected}"
				${isDisabled ? 'disabled aria-disabled="true"' : ''}>
				${image ? `<span class="mm-lt-option-image">${image}</span>` : ''}
				<span class="mm-lt-option-label">
					<span class="mm-lt-option-title text">${this._escape(this._label(opt))}</span>
					${opt.description ? `<span class="text-sm mm-black-500">${this._escape(opt.description)}</span>` : ''}
				</span>
				${priceText ? `<span class="mm-lt-option-meta"><span class="text-sm mm-semibold">${this._escape(priceText)}</span></span>` : ''}
			</button>
			${restricted}
		`;
	},

	/* ====================================================================== */
	/* RENDER                                                                  */
	/* ====================================================================== */

	_render() {
		const stepKey = this.state.steps[this.state.stepIndex];
		this._updateHeader();

		switch (stepKey) {
			case 'correction':
				this._renderCorrection();
				break;
			case 'ordonnance':
				this._renderOrdonnance();
				break;
			case 'combo':
				this._renderCombo();
				break;
			case 'art_detail':
				this._renderArtDetail();
				break;
			case 'gravure':
				this._renderEngraving();
				break;
			case 'recap':
				this._renderRecap();
				break;
			default:
				this._renderStandardStep(stepKey);
		}

		this._updateFooter(stepKey);
		this._announce(this._stepTitle(stepKey));
	},

	_renderLoading() {
		if (!this.dom.content) return;
		this.dom.content.innerHTML = `<div class="mm-lt-error">${this._t('aria.progress', 'Chargement…')}</div>`;
	},

	_renderError(msg) {
		this.dom.content.innerHTML = `<div class="mm-lt-error" role="alert">${this._escape(msg)}</div>`;
	},

	_updateHeader() {
		const total = this.state.steps.length;
		const current = this.state.stepIndex + 1;
		const stepKey = this.state.steps[this.state.stepIndex];

		// Title
		if (this.dom.title) {
			this.dom.title.textContent = this._t('title', 'Personnalisation');
		}

		// Progress bar
		if (this.dom.progress) {
			this.dom.progress.setAttribute('aria-valuenow', String(current));
			this.dom.progress.setAttribute('aria-valuemax', String(total));
			const label = this._t('progress', `Étape ${current} sur ${total}`)
				.replace('{{ current }}', current)
				.replace('{{ total }}', total);
			this.dom.progress.setAttribute('aria-valuetext', `${label} — ${this._stepTitle(stepKey)}`);
		}
		if (this.dom.progressBar) {
			this.dom.progressBar.style.width = `${(current / total) * 100}%`;
		}

		// Back button visibility
		if (this.dom.backBtn) {
			this.dom.backBtn.hidden = this.state.stepIndex === 0;
		}
	},

	_renderCorrection() {
		const options = this.data.parcours.map(p => ({
			id: p.id,
			label: p.label,
			price: 0,
			hasValid: true,
			hasExcluded: false,
			hasMissingOptimum: false,
		}));
		this._renderOptionList('correction', this._t('step.type_verre.title', 'Choix de la correction'), this._t('step.type_verre.description', ''), options);
		// Pour la 1re étape, remplacer titre/description par celle de "correction"
		const html = `
			<div class="text-l mm-centered">
				<h2 class="text-l">${this._escape(this._t('step.recap.title', 'Choisissez votre correction'))}</h2>
			</div>
			${this._buildOptionListHTML('correction', options)}
		`;
		this.dom.content.innerHTML = html;
	},

	_renderStandardStep(stepKey) {
		const options = this._getOptionsForStep(stepKey);
		this._renderOptionList(
			stepKey,
			this._t(`step.${stepKey}.title`, stepKey),
			this._t(`step.${stepKey}.description`, ''),
			options
		);
	},

	_renderOptionList(stepKey, title, description, options) {
		const isSwatchStep = stepKey === 'couleur';
		const html = `
			<div class="text-l mm-centered">
				<h2 class="text-l">${this._escape(title)}</h2>
				${description ? `<p class="text-sm mm-black-500">${this._escape(description)}</p>` : ''}
			</div>
			${this._buildOptionListHTML(stepKey, options, isSwatchStep)}
		`;
		this.dom.content.innerHTML = html;
	},

	_buildOptionListHTML(stepKey, options, isSwatchStep = false) {
		if (!options.length) {
			return `<div class="mm-lt-error">${this._escape(this._t('errors.no_combination', 'Aucune combinaison disponible.'))}</div>`;
		}
		const items = options.map(opt => this._buildOptionButton(stepKey, opt, isSwatchStep)).join('');
		const groupClass = isSwatchStep ? 'mm-lt-options mm-lt-options--swatches' : 'mm-lt-options';
		return `<div class="${groupClass}" role="group" aria-label="${this._escape(this._t('aria.step_options', 'Options'))}">${items}</div>`;
	},

	_buildOptionButton(stepKey, opt, isSwatch) {
		const isSelected = this.state.selections[stepKey] === opt.id;
		const disabled = !opt.hasValid;
		const meta = this._getOptionMeta(opt);
		const hasMeta = !!(meta && (meta.image || meta.description || meta.badge));
		const stateClasses = [
			'mm-lt-option',
			hasMeta ? 'mm-lt-option--rich' : '',
			isSelected ? 'mm-lt-option--selected' : '',
			disabled ? 'mm-lt-option--disabled' : '',
		].filter(Boolean).join(' ');

		let hint = '';
		if (disabled && opt.hasExcluded) hint = this._t('option.excluded', '');
		else if (disabled && opt.hasMissingOptimum) hint = this._t('option.coming_soon', '');

		const showPrice = stepKey !== 'couleur' && stepKey !== 'correction';
		const priceText = showPrice ? (opt.price > 0
			? `<span class="text-sm mm-semibold">+${opt.price}€</span>`
			: `<span class="text-sm mm-semibold mm-black-500">${this._t('option.included', '')}</span>`) : '';

		const swatchEl = isSwatch
			? `<span class="mm-lt-swatch" style="background:${this._swatchColor(opt.id)};" aria-hidden="true"></span>`
			: '';

		const imageEl = (!isSwatch && meta?.image)
			? `<span class="mm-lt-option-image"><img src="${this._escape(meta.image)}" alt="${this._escape(meta.image_alt || '')}" loading="lazy" decoding="async"></span>`
			: '';

		const description = meta?.description
			? `<span class="text-sm mm-black-500">${this._escape(meta.description)}</span>`
			: '';

		const badge = meta?.badge
			? `<span class="mm-lt-option-badge text-xs black-500">${this._escape(meta.badge)}</span>`
			: '';

		return `
			<button
				type="button"
				class="${stateClasses}"
				data-lens-option
				data-step-key="${this._escape(stepKey)}"
				data-option-id="${this._escape(opt.id)}"
				data-option-label="${this._escape(opt.label)}"
				aria-pressed="${isSelected}"
				${disabled ? 'disabled aria-disabled="true"' : ''}
				${hint ? `aria-describedby="hint-${stepKey}-${opt.id}"` : ''}>
				${swatchEl}${imageEl}
				<span class="mm-lt-option-label">
					<span class="text">${this._escape(this._label(opt))}</span>
					${description}
					${badge}
				</span>
				<span class="mm-lt-option-meta">
					${priceText}
				</span>
			</button>
			${hint ? `<span class="mm-lt-option-hint text-xs mm-black-500 mm-centered" id="hint-${stepKey}-${opt.id}">${this._escape(hint)}</span>` : ''}
		`;
	},

	/**
	 * Récupère les métadonnées d'une option (block lens_option) en cherchant
	 * en cascade : d'abord par id (slug handleize), puis par label normalisé
	 * en lowercase. Permet de matcher quand le slug Liquid (handleize du label
	 * tapé par le client) diffère de l'id du JSON (ex: "Vision de loin" →
	 * handleize="vision-de-loin" mais id JSON="vision-loin").
	 */
	_getOptionMeta(opt) {
		if (!opt) return null;
		const meta = window.MM_LT_OPTIONS_META || {};
		if (opt.id && meta[opt.id]) return meta[opt.id];
		if (opt.label) {
			const key = String(opt.label).toLowerCase().trim();
			if (meta[key]) return meta[key];
		}
		return null;
	},

	/**
	 * SVG inline de la forme de verre — fill + fill-opacity dynamiques.
	 * Utilisé pour la preview du combo et (en x2) pour la step gravure.
	 *
	 * Mode dégradé : si `gradient=true`, applique un linearGradient vertical
	 * (haut = 75 % × opacity, bas = 18 % × opacity) au lieu d'un fill plat.
	 * L'`uniqueId` évite les conflits d'id quand plusieurs SVG sont rendus
	 * sur la même page (ex: combo + 2 verres gravure).
	 */
	_buildLensPreviewSvg(color, opacity, gradient = false, uniqueId = 'default') {
		const fillColor = color || '#cdc8c4';
		const baseOpacity = typeof opacity === 'number' ? opacity : 0;
		const path = 'M119.108 0.649837C135.954 2.00485 154.722 6.48901 168.395 17.3032C173.537 21.3975 177.802 29.5838 178.103 36.1684C180.032 62.5547 163.778 104.278 139.289 118.996C101.791 141.528 13.4514 130.975 2.33796 79.4758C-1.4479 62.2411 -1.90917 38.4133 10.8053 24.354C29.6316 3.53566 84.8886 -2.10255 119.108 0.649837Z';

		if (gradient) {
			const topOpacity = (0.75 * baseOpacity).toFixed(3);
			const bottomOpacity = (0.18 * baseOpacity).toFixed(3);
			const gradId = `mm-lt-lens-grad-${uniqueId}`;
			return `
				<svg class="mm-lt-lens-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 179 131" preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden="true">
					<defs>
						<linearGradient id="${this._escape(gradId)}" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0" stop-color="${this._escape(fillColor)}" stop-opacity="${topOpacity}"/>
							<stop offset="1" stop-color="${this._escape(fillColor)}" stop-opacity="${bottomOpacity}"/>
						</linearGradient>
					</defs>
					<path d="${path}" fill="url(#${this._escape(gradId)})"/>
				</svg>
			`;
		}
		return `
			<svg class="mm-lt-lens-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 179 131" preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden="true">
				<path d="${path}" fill="${this._escape(fillColor)}" fill-opacity="${baseOpacity.toFixed(2)}"/>
			</svg>
		`;
	},

	/**
	 * Convertit un hex (#rgb / #rrggbb) en string "r, g, b" pour usage dans rgba().
	 * Fallback "204, 200, 196" (gris doux) si le hex est invalide.
	 */
	_hexToRgbString(hex) {
		const s = String(hex || '').replace('#', '').trim();
		let r = 204, g = 200, b = 196;
		if (s.length === 3) {
			r = parseInt(s[0] + s[0], 16);
			g = parseInt(s[1] + s[1], 16);
			b = parseInt(s[2] + s[2], 16);
		} else if (s.length === 6) {
			r = parseInt(s.slice(0, 2), 16);
			g = parseInt(s.slice(2, 4), 16);
			b = parseInt(s.slice(4, 6), 16);
		}
		if ([r, g, b].some(v => Number.isNaN(v))) return '204, 200, 196';
		return `${r}, ${g}, ${b}`;
	},

	/**
	 * Background CSS d'une pastille couleur.
	 * @param {string} color - hex (#rrggbb)
	 * @param {boolean} gradient - si true, applique un linearGradient vertical (75% → 18%)
	 * @param {number} alphaMultiplier - multiplie l'opacité finale (1 par défaut). Permet
	 *   de cumuler l'effet "intensité solaire" (30/60/90%) dans le background lui-même
	 *   sans appliquer `opacity:X` sur l'élément (qui affecterait aussi le box-shadow).
	 */
	_buildSwatchBackground(color, gradient, alphaMultiplier = 1) {
		const rgb = this._hexToRgbString(color);
		if (!gradient) {
			return alphaMultiplier >= 1 ? color : `rgba(${rgb}, ${alphaMultiplier})`;
		}
		const top = (0.75 * alphaMultiplier).toFixed(3);
		const bottom = (0.18 * alphaMultiplier).toFixed(3);
		return `linear-gradient(to bottom, rgba(${rgb}, ${top}), rgba(${rgb}, ${bottom}))`;
	},

	_swatchColor(id) {
		const map = {
			gris: '#9aa0a3', brun: '#7a4e2b', 'gris-vert': '#6b7064',
			violet: '#7b4ea8', bleu: '#2a6db5', orange: '#d67a3c',
			aube: '#c2b7d6', crepuscule: '#5c4a78', feu: '#c64a2a',
			lagon: '#3aa3b8', metal: '#9a9a9a', meteore: '#3a3a3a',
			ocean: '#1d4f78', olympe: '#d7c46a', sirius: '#bcbfd8', titane: '#7c7c7c',
			vert: '#4a6b3a', rouge: '#a83232', jaune: '#d9b94a', noir: '#1a1a1a',
			blanc: '#f0f0f0', rose: '#d68aa8', ambre: '#c98c3c', miel: '#c69b5e',
			turquoise: '#3aa3a8', marine: '#1a3a5c',
			/* Couleurs "luxe" (parcours progressifs > teintes au soleil > luxe).
			 * Inspiration pierres precieuses, hex distincts des couleurs proches. */
			saphir: '#1e5fa8', emeraude: '#2d8055', amethyste: '#8e57b8', rubis: '#b02e3e',
		};
		/* Fallback : hex valide (utilisé comme fill SVG → un linear-gradient CSS casserait
		 * le SVG car ce n'est pas un paint SVG valide, seul url(#gradientId) l'est). */
		return map[id] || '#9aa0a3';
	},

	/* ====================================================================== */
	/* COMBO STEP (catégorie + style + couleur + intensité)                    */
	/* ====================================================================== */

	_renderCombo() {
		const cfg = this.state.comboSettings || {};

		// Pré-sélection du 1er valide de chaque dimension à l'arrivée sur le combo.
		this._initComboSelections();
		const error = this.state.comboError;

		// Listing combo : chaque dimension affiche TOUTES ses valeurs possibles dans le
		// parcours, indépendamment des autres sélections combo. Compatibilité avec les
		// autres sélections combo en cours = `hasValid` (sinon option grisée, pas masquée).
		const categories  = this._getComboOptions('categorie');
		const styles      = this._getComboOptions('sous_categorie');
		const colors      = this._getComboOptions('couleur');
		const intensities = this._getComboOptions('cat_solaire');

		const selCat = this.state.selections.categorie;
		const selStyle = this.state.selections.sous_categorie;
		const selColor = this.state.selections.couleur;
		const selIntensity = this.state.selections.cat_solaire;

		const colorMeta = selColor ? (window.MM_LT_COLORS_META || {})[selColor] : null;
		const selColorOpt = colors.find(o => o.id === selColor);
		const colorLabel = (selColorOpt ? this._label(selColorOpt) : '')
			|| colorMeta?.label
			|| '';
		// Fallback en cascade : meta block lens_color → swatch mapping local → gris.
		// Garantit que les pastilles d'intensité prennent toujours la couleur sélectionnée.
		const overlayColor = colorMeta?.color || (selColor ? this._swatchColor(selColor) : '#666');
		// Si la dimension cat_solaire n'a aucune option dans ce parcours+catégorie
		// (ex: polarisant / miroir n'ont pas d'intensité), on affiche la couleur à
		// 75 % (plus réaliste qu'une pleine opacité) pour que le verre reste visible
		// sans paraître trop intense.
		const overlayOpacity = selIntensity
			? (this.config.comboIntensityOpacity[selIntensity] || 0)
			: (intensities.length === 0 ? 0.75 : 0);
		// Mode dégradé : sous_categorie = 'degrade' → preview SVG + pastilles couleur
		// rendus avec un linearGradient vertical (75 % top → 18 % bottom).
		const useGradient = selStyle === 'degrade';

		this.dom.content.innerHTML = `
			<div class="mm-lt-combo">
				${error ? `<div class="mm-lt-ordonnance-error text-sm" role="alert">${this._escape(error)}</div>` : ''}

				${cfg.slogan ? `<p class="mm-lt-combo-slogan">${this._escape(cfg.slogan)}</p>` : ''}

				<div class="mm-lt-combo-preview">
					${this._buildLensPreviewSvg(overlayColor, overlayOpacity, useGradient, 'combo')}
				</div>

				${categories.length ? this._renderComboPills(categories, selCat) : ''}

				<div class="mm-lt-combo-panel">
					${styles.length ? this._renderComboToggle(styles, selStyle) : ''}

					${colors.length ? this._renderComboColors(colors, selColor, colorLabel, useGradient) : ''}

					${intensities.length ? this._renderComboIntensities(intensities, selIntensity, overlayColor, useGradient) : ''}
				</div>
			</div>
		`;
	},

	_renderComboPills(categories, selectedId) {
		const single = categories.length === 1;
		const items = categories.map(c => {
			const disabled = !c.hasValid;
			const selected = c.id === selectedId;
			const label = this._label(c);
			const priceLabel = c.price > 0 ? ` +${c.price}€` : '';
			const cls = [
				'mm-lt-combo-pill',
				selected ? 'is-selected' : '',
				(disabled || single) ? 'is-disabled' : '',
			].filter(Boolean).join(' ');
			return `
				<button type="button"
					class="${cls}"
					data-lens-option
					data-step-key="categorie"
					data-option-id="${this._escape(c.id)}"
					data-option-label="${this._escape(label)}"
					aria-pressed="${selected}"
					${disabled || single ? 'disabled aria-disabled="true"' : ''}>
					${this._escape(label)}${priceLabel}
				</button>
			`;
		}).join('');
		return `<div class="mm-lt-combo-pills" role="radiogroup" aria-label="${this._escape(this._t('combo.category_label', 'Catégorie'))}">${items}</div>`;
	},

	_renderComboToggle(styles, selectedId) {
		const single = styles.length === 1;
		const items = styles.map(s => {
			const disabled = !s.hasValid;
			const selected = s.id === selectedId;
			const label = this._label(s);
			const priceLabel = s.price > 0 ? ` +${s.price}€` : '';
			const cls = [
				'mm-lt-combo-toggle-btn',
				selected ? 'is-selected' : '',
				(disabled || single) ? 'is-disabled' : '',
			].filter(Boolean).join(' ');
			return `
				<button type="button"
					class="${cls}"
					data-lens-option
					data-step-key="sous_categorie"
					data-option-id="${this._escape(s.id)}"
					data-option-label="${this._escape(label)}"
					aria-pressed="${selected}"
					${disabled || single ? 'disabled aria-disabled="true"' : ''}>
					${this._escape(label)}${priceLabel}
				</button>
			`;
		}).join('');
		return `<div class="mm-lt-combo-toggle" role="radiogroup" aria-label="${this._escape(this._t('combo.style_label', 'Style'))}">${items}</div>`;
	},

	_renderComboColors(colors, selectedId, selectedLabel, gradientMode = false) {
		const single = colors.length === 1;
		const label = selectedLabel || this._t('combo.color_selected_label', 'Couleur sélectionnée');
		const items = colors.map(c => {
			const meta = (window.MM_LT_COLORS_META || {})[c.id] || {};
			const bgColor = meta.color || this._swatchColor(c.id);
			const bgImage = meta.image;
			const disabled = !c.hasValid;
			const selected = c.id === selectedId;
			const colorLabel = this._label(c);
			const cls = [
				'mm-lt-combo-color',
				selected ? 'is-selected' : '',
				(disabled || single) ? 'is-disabled' : '',
			].filter(Boolean).join(' ');
			const style = bgImage
				? `background-image:url(${this._escape(bgImage)});background-color:${this._escape(bgColor)};`
				: `background:${this._escape(this._buildSwatchBackground(bgColor, gradientMode))};`;
			return `
				<button type="button"
					class="${cls}"
					data-lens-option
					data-step-key="couleur"
					data-option-id="${this._escape(c.id)}"
					data-option-label="${this._escape(colorLabel)}"
					aria-label="${this._escape(colorLabel)}"
					aria-pressed="${selected}"
					${disabled || single ? 'disabled aria-disabled="true"' : ''}
					style="${style}"></button>
			`;
		}).join('');
		return `
			<div class="mm-lt-combo-section">
				<p class="text-sm mm-centered">${this._escape(label)}</p>
				<div class="mm-lt-combo-colors" role="radiogroup" aria-label="${this._escape(this._t('step.couleur.title', 'Couleur'))}">${items}</div>
			</div>
		`;
	},

	_renderComboIntensities(intensities, selectedId, overlayColor, gradientMode = false) {
		const single = intensities.length === 1;
		const items = intensities.map(i => {
			const opacity = this.config.comboIntensityOpacity[i.id] ?? 0.5;
			const disabled = !i.hasValid;
			const selected = i.id === selectedId;
			const pct = Math.round(opacity * 100);
			const cls = [
				'mm-lt-combo-intensity',
				selected ? 'is-selected' : '',
				(disabled || single) ? 'is-disabled' : '',
			].filter(Boolean).join(' ');
			// Background calculé en rgba avec l'opacity baked dedans (au lieu d'appliquer
			// opacity:X sur l'élément) → le box-shadow du cercle sélectionné reste à
			// pleine opacité, seule la couleur du dot est transparente.
			const dotBg = this._buildSwatchBackground(overlayColor, gradientMode, opacity);
			const intensityLabel = this._label(i);
			return `
				<button type="button"
					class="${cls}"
					data-lens-option
					data-step-key="cat_solaire"
					data-option-id="${this._escape(i.id)}"
					data-option-label="${this._escape(intensityLabel)}"
					aria-label="${this._escape(intensityLabel)} (${pct}%)"
					aria-pressed="${selected}"
					${disabled || single ? 'disabled aria-disabled="true"' : ''}>
					<span class="mm-lt-combo-intensity-dot" style="background:${this._escape(dotBg)};"></span>
					<span class="mm-lt-combo-intensity-label">${pct}%</span>
				</button>
			`;
		}).join('');
		return `<div class="mm-lt-combo-intensities" role="radiogroup" aria-label="${this._escape(this._t('combo.intensity_label', 'Intensité'))}">${items}</div>`;
	},

	_validateCombo() {
		// Toutes les dimensions applicables doivent être renseignées ET la combinaison
		// finale doit exister (= au moins un optimum_ref valide pour cet état).
		const subs = this.config.comboSubsteps;
		for (const sub of subs) {
			const opts = this._getComboOptions(sub);
			if (opts.length === 0) continue; // dimension non applicable (ex: transparents)
			if (!this.state.selections[sub]) {
				this.state.comboError = this._t('combo.error_required', 'Veuillez compléter toutes les options pour continuer.');
				return false;
			}
		}
		if (!this._getFinalCombination()) {
			this.state.comboError = this._t('combo.error_required', 'Veuillez compléter toutes les options pour continuer.');
			return false;
		}
		this.state.comboError = null;
		return true;
	},

	/**
	 * Liste les options d'un sous-step combo selon une cascade DESCENDANTE STRICTE :
	 * chaque dimension est filtrée par les sélections AMONT uniquement (jamais aval).
	 *
	 * Ordre : catégorie → sous_categorie → couleur → cat_solaire.
	 *
	 * - Catégorie : libre, toutes les catégories du parcours sont listées et cliquables.
	 * - Style : filtré par la catégorie sélectionnée.
	 * - Couleur : filtrée par catégorie + style.
	 * - Intensité : filtrée par catégorie + style + couleur.
	 *
	 * Le clic sur une dimension reset les sélections aval (cascade dans _cascadeResetCombo)
	 * et _initComboSelections re-sélectionne la 1re valeur valide à chaque render → on
	 * peut toujours librement changer de catégorie.
	 */
	_getComboOptions(subStep) {
		const comboSubs = this.config.comboSubsteps;
		const idx = comboSubs.indexOf(subStep);
		if (idx < 0) return [];
		// Exclure subStep lui-même + toutes les dimensions AVAL du filtrage.
		const excludeKeys = comboSubs.slice(idx);
		const compatible = this._getCompatibleCombinations(excludeKeys);
		const map = new Map();
		for (const c of compatible) {
			const opt = c[subStep];
			if (!opt) continue;
			if (!map.has(opt.id)) {
				map.set(opt.id, {
					id: opt.id,
					label: opt.label,
					price: opt.price || 0,
					perce_only: opt.perce_only === true,
					not_perce:  opt.not_perce  === true,
					hasValid: false,
					hasExcluded: false,
					hasMissingOptimum: false,
				});
			}
			const entry = map.get(opt.id);
			if (c.exclu) entry.hasExcluded = true;
			else if (!c.optimum_ref) entry.hasMissingOptimum = true;
			else entry.hasValid = true;
		}
		return this._filterByPerceRule([...map.values()]);
	},

	/**
	 * À l'arrivée sur l'étape combo, pré-sélectionne la 1re option valide de chaque
	 * dimension (cascade : catégorie → style → couleur → intensité). Ne touche pas
	 * aux sélections déjà faites par l'utilisateur (back-forward ne réinitialise pas).
	 */
	_initComboSelections() {
		const subs = this.config.comboSubsteps;
		for (const sub of subs) {
			if (this.state.selections[sub]) continue;
			const opts = this._getComboOptions(sub);
			/* Pour l'intensité solaire : préférer cat-3 (90%) par défaut, fallback 1re valide */
			if (sub === 'cat_solaire') {
				const preferred = opts.find(o => o.hasValid && o.id === 'cat-3');
				const fallback = opts.find(o => o.hasValid);
				if (preferred) this.state.selections[sub] = preferred.id;
				else if (fallback) this.state.selections[sub] = fallback.id;
				continue;
			}
			const firstValid = opts.find(o => o.hasValid);
			if (firstValid) {
				this.state.selections[sub] = firstValid.id;
			}
		}
	},

	/* ====================================================================== */
	/* ENGRAVING                                                               */
	/* ====================================================================== */

	_renderEngraving() {
		if (!this.state.gravureEnabled) {
			this.goNext();
			return;
		}
		this._ensureEngraving();
		const eng = this.state.engraving;
		// Couleur + intensité héritées du combo pour rendre la même teinte sur le SVG.
		// Si la catégorie sélectionnée n'a pas de cat_solaire (polarisant, miroir),
		// on affiche la couleur en pleine opacité.
		const selColor = this.state.selections.couleur;
		const selIntensity = this.state.selections.cat_solaire;
		const colorMeta = selColor ? this._getOptionMeta({ id: selColor, label: selColor }) : null;
		const lensSvgColor = colorMeta?.color || (selColor ? this._swatchColor(selColor) : '#cdc8c4');
		const hasIntensityDim = this._getComboOptions('cat_solaire').length > 0;
		const lensSvgOpacity = selIntensity
			? (this.config.comboIntensityOpacity[selIntensity] || 0)
			: (hasIntensityDim ? 0 : 0.75);
		const lensSvgGradient = this.state.selections.sous_categorie === 'degrade';

		// Si un art_detail avec price_eur > 0 est sélectionné, on n'affiche plus le prix
		// sur les pills couleur (le prix réel vient de l'art_detail × nb zones).
		// Sinon (legacy / pas d'art_detail), on affiche les prix par couleur.
		const artOpt = this.state.artDetail
			? (this.state.artDetailOptions || []).find(o => o.id === this.state.artDetail)
			: null;
		const showPillPrices = !(artOpt && typeof artOpt.price_eur === 'number' && artOpt.price_eur > 0);
		const colors = ['transparent', 'gold', 'silver'];
		const colorPills = colors.map(c => {
			const price = this.state.engravingPrices[c] || 0;
			const selected = eng.color === c;
			const priceLabel = showPillPrices && price > 0 ? ` +${price}€` : '';
			return `
				<button type="button"
					class="mm-lt-eng-color-pill ${selected ? 'is-selected' : ''}"
					data-lens-eng-color="${c}"
					aria-pressed="${selected}">
					${this._escape(this._t(`engraving.color_${c}`, c))}${priceLabel}
				</button>
			`;
		}).join('');

		const zones = ['lt', 'lb', 'rt', 'rb'];
		// Compte les zones ACTIVÉES (peu importe le texte) pour le placeholder + summary.
		// Le calcul du prix (_getEngravingTotal) reste basé sur les zones avec texte valide.
		const zoneCount = zones.filter(k => eng.zones[k] !== null).length;
		const lensCount =
			((eng.zones.lt !== null || eng.zones.lb !== null) ? 1 : 0) +
			((eng.zones.rt !== null || eng.zones.rb !== null) ? 1 : 0);
		const total = this._getEngravingTotal();

		// Summary "X zone · Y verre gravé · Z€" : affiche UNIQUEMENT s'il y a au moins
		// une zone activée. Reste positionné SOUS la preview, au-dessus des pills couleur.
		const summary = zoneCount > 0 ? `
			<p class="mm-lt-eng-summary">
				<span>${zoneCount} ${this._escape(zoneCount > 1 ? 'zones' : 'zone')}</span>
				<span aria-hidden="true">·</span>
				<span>${lensCount} ${this._escape(lensCount > 1 ? 'verres gravés' : 'verre gravé')}</span>
				<span aria-hidden="true">·</span>
				<span class="mm-lt-eng-summary-price">${total}€</span>
			</p>
		` : '';

		// Placeholder "Aucune zone sélectionnée" : affiché SOUS les pills couleur
		// uniquement quand 0 zone active (prend la place qu'auraient les cards).
		const emptyPlaceholder = zoneCount === 0
			? `<p class="mm-lt-eng-empty">${this._escape(this._t('engraving.no_zone_selected', 'Aucune zone sélectionnée'))}</p>`
			: '';

		const activeZoneCards = zones
			.filter(k => eng.zones[k] !== null)
			.map(k => this._buildEngravingZoneCard(k, eng.zones[k]))
			.join('');

		this.dom.content.innerHTML = `
			<div class="mm-lt-step-header">
				<h2 class="text-l mm-centered">${this._escape(this._t('engraving.title', this._t('step.gravure.title', 'Gravure sur verres')))}</h2>
				${this.state.engravingSlogan ? `<p class="text-sm mm-black-500 mm-centered">${this._escape(this.state.engravingSlogan)}</p>` : ''}
			</div>

			<div class="mm-lt-eng-preview">
				${this._buildEngravingLens('left', eng.zones, lensSvgColor, lensSvgOpacity, lensSvgGradient)}
				${this._buildEngravingLens('right', eng.zones, lensSvgColor, lensSvgOpacity, lensSvgGradient)}
			</div>

			${summary}

			<div class="mm-lt-eng-colors" role="radiogroup" aria-label="${this._escape(this._t('engraving.color_label', 'Couleur'))}">
				${colorPills}
			</div>

			${emptyPlaceholder}

			<div class="mm-lt-eng-cards">${activeZoneCards}</div>
		`;
	},

	_buildEngravingLens(side, zones, color, opacity, gradient = false) {
		const topKey = side === 'left' ? 'lt' : 'rt';
		const botKey = side === 'left' ? 'lb' : 'rb';
		return `
			<div class="mm-lt-eng-lens mm-lt-eng-lens--${side}">
				${this._buildLensPreviewSvg(color, opacity, gradient, `eng-${side}`)}
				${this._buildEngravingZoneBtn(topKey, zones[topKey], 'top')}
				${this._buildEngravingZoneBtn(botKey, zones[botKey], 'bottom')}
			</div>
		`;
	},

	_buildEngravingZoneBtn(zoneKey, zoneData, position) {
		// État visuel "coché" dès l'activation (peu importe le texte). Le calcul prix
		// reste basé sur _isZoneEngraved (zone active + texte non-vide + ≤ max chars).
		const isActive = zoneData !== null;
		const wrapCls = `mm-lt-eng-zone-wrap mm-lt-eng-zone-wrap--${position}`;
		const btnCls = 'mm-lt-eng-zone' + (isActive ? ' is-engraved' : '');
		const label = this._t(`engraving.zone_${zoneKey}`, zoneKey);
		const icon = isActive
			? `<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M2.61185 7.03187L2.13043 6.55045L6.55048 2.1304L7.0319 2.61182L2.61185 7.03187Z" fill="white"/>
			<path d="M0.000752842 4.41929L0.48217 3.93788L3.10926 6.56497L2.62785 7.04639L0.000752842 4.41929Z" fill="white"/>
			</svg>
			`
			: `<svg width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M3.01623 7.04762L3.69706 7.04762L3.69706 -2.97599e-08L3.01623 0L3.01623 7.04762Z" fill="black"/>
				<path d="M6.7072 3.86719L6.7072 3.18636L-5.95197e-08 3.18636L0 3.86719L6.7072 3.86719Z" fill="black"/>
				</svg>
			`;
		// Bouton "−" en haut-droite, visible uniquement si la zone est activée.
		const removeBtn = isActive ? `
			<button type="button"
				class="mm-lt-eng-zone-remove-hot"
				data-lens-eng-zone-remove="${zoneKey}"
				aria-label="${this._escape(this._t('engraving.remove_zone', 'Retirer'))} ${this._escape(label)}">
				<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>
			</button>
		` : '';
		return `
			<span class="${wrapCls}">
				<button type="button"
					class="${btnCls}"
					data-lens-eng-zone="${zoneKey}"
					aria-label="${this._escape(label)}"
					aria-pressed="${isActive}">
					${icon}
				</button>
				${removeBtn}
			</span>
		`;
	},

	/**
	 * Mini-icône (41×14) qui représente les 2 verres et positionne un point noir
	 * sur le verre + position correspondant à la zone (lt/lb/rt/rb).
	 * Utilisée dans le header de chaque card de gravure pour donner un repère
	 * visuel immédiat de la zone concernée.
	 */
	_buildEngravingZoneIcon(zoneKey) {
		const lensLeftPath  = 'M5.89722 0.0653578C4.21762 0.201647 2.34652 0.652673 0.983282 1.74039C0.470684 2.1522 0.045435 2.97559 0.0154648 3.63788C-0.176931 6.29187 1.44367 10.4885 3.88517 11.9689C7.6237 14.2351 16.4311 13.1737 17.5391 7.99382C17.9165 6.26033 17.9625 3.86368 16.6949 2.44956C14.8179 0.35562 9.30885 -0.211483 5.89722 0.0653578Z';
		const lensRightPath = 'M34.7395 0.0653578C36.4191 0.201647 38.2902 0.652673 39.6534 1.74039C40.166 2.1522 40.5913 2.97559 40.6213 3.63788C40.8137 6.29187 39.193 10.4885 36.7515 11.9689C33.013 14.2351 24.2056 13.1737 23.0976 7.99382C22.7202 6.26033 22.6742 3.86368 23.9418 2.44956C25.8188 0.35562 31.3279 -0.211483 34.7395 0.0653578Z';
		// Positions du point noir (rect 2.78x2.78) par zone — côté extérieur du verre,
		// en haut ou en bas selon la zone choisie.
		const dotPositions = {
			lt: { x: 2,     y: 1.67 },   // verre gauche, haut-extérieur
			lb: { x: 2,     y: 9.5  },   // verre gauche, bas-extérieur
			rt: { x: 35.85, y: 1.67 },   // verre droit,  haut-extérieur
			rb: { x: 35.85, y: 9.5  },   // verre droit,  bas-extérieur
		};
		const pos = dotPositions[zoneKey] || dotPositions.lt;
		return `
			<svg width="41" height="14" viewBox="0 0 41 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
				<path d="${lensLeftPath}" fill="#E8E8E8"/>
				<path d="${lensRightPath}" fill="#E8E8E8"/>
				<rect x="${pos.x}" y="${pos.y}" width="2.77764" height="2.77764" rx="1.38882" fill="#0B0B0B"/>
			</svg>
		`;
	},

	_buildEngravingZoneCard(zoneKey, zone) {
		const label = this._t(`engraving.zone_${zoneKey}`, zoneKey);
		// "Œil Gauche Haut" → split title (Œil Gauche) + position (Haut)
		const parts = label.split(' ');
		const positionWord = parts[parts.length - 1];
		const eyeWord = parts.slice(0, -1).join(' ');
		const max = this.config.engravingMaxLength;
		const isOverLimit = zone.text && zone.text.length > max;
		const fonts = ['aa1', 'aa2', 'aa3', 'aa4'];
		const fontBtns = fonts.map(f => `
			<button type="button"
				class="mm-lt-eng-font-btn mm-lt-eng-font-${f} ${zone.font === f ? 'is-selected' : ''}"
				data-lens-eng-font="${f}"
				data-lens-eng-zone-target="${zoneKey}"
				aria-pressed="${zone.font === f}"
				aria-label="${this._escape(this._t('engraving.font_label', 'Style'))} ${f.toUpperCase()}">
				Aa
			</button>
		`).join('');

		return `
			<div class="mm-lt-eng-card ${isOverLimit ? 'is-error' : ''}" data-lens-eng-card="${zoneKey}">
				<div class="mm-lt-eng-card-header">
					<span class="mm-lt-eng-card-icon" aria-hidden="true">${this._buildEngravingZoneIcon(zoneKey)}</span>
					<span class="text-sm">${this._escape(eyeWord)}</span>
					<span class="text-sm">${this._escape(positionWord)}</span>
					<button type="button" class="mm-lt-eng-card-remove text-sm mm-black-500" data-lens-eng-zone-remove="${zoneKey}">
						${this._escape(this._t('engraving.remove_zone', 'Retirer'))}
					</button>
				</div>
				<div class="mm-lt-eng-card-input-wrap">
					<input type="text"
						class="mm-lt-eng-card-input mm-lt-eng-font-${zone.font || 'aa1'}"
						data-lens-eng-zone-text="${zoneKey}"
						value="${this._escape(zone.text || '')}"
						placeholder="${this._escape(this._t('engraving.text_placeholder', 'Votre texte'))}"
						aria-label="${this._escape(label)}">
					${isOverLimit ? `<button type="button" class="mm-lt-eng-card-clear" data-lens-eng-zone-clear="${zoneKey}" aria-label="Effacer">×</button>` : ''}
				</div>
				${isOverLimit ? `<p class="mm-lt-eng-card-error" role="alert">${this._escape(this._t('engraving.text_max_error', `${max} caractères maximum`).replace('{{ max }}', max))}</p>` : ''}
				<div class="mm-lt-eng-card-fonts" role="radiogroup" aria-label="${this._escape(this._t('engraving.font_label', 'Style de police'))}">
					${fontBtns}
				</div>
			</div>
		`;
	},

	_ensureEngraving() {
		if (!this.state.engraving) {
			this.state.engraving = {
				color: 'transparent',
				zones: { lt: null, lb: null, rt: null, rb: null },
			};
		}
	},

	_toggleEngravingZone(zoneKey) {
		this._ensureEngraving();
		if (this.state.engraving.zones[zoneKey] === null) {
			this.state.engraving.zones[zoneKey] = { text: '', font: 'aa1' };
		} else {
			this.state.engraving.zones[zoneKey] = null;
		}
		this._render();
	},

	_removeEngravingZone(zoneKey) {
		if (!this.state.engraving) return;
		this.state.engraving.zones[zoneKey] = null;
		this._render();
	},

	_clearEngravingZoneText(zoneKey) {
		if (!this.state.engraving || !this.state.engraving.zones[zoneKey]) return;
		this.state.engraving.zones[zoneKey].text = '';
		this._render();
	},

	_setEngravingColor(color) {
		this._ensureEngraving();
		this.state.engraving.color = color;
		this._render();
	},

	_setEngravingZoneFont(zoneKey, font) {
		if (!this.state.engraving || !this.state.engraving.zones[zoneKey]) return;
		this.state.engraving.zones[zoneKey].font = font;
		this._render();
	},

	_updateEngravingZoneText(zoneKey, value) {
		if (!this.state.engraving || !this.state.engraving.zones[zoneKey]) return;
		// Pas de slice : on laisse l'utilisateur taper plus que max pour qu'il VOIE l'erreur.
		this.state.engraving.zones[zoneKey].text = value;
		// Re-render pour mettre à jour l'icône zone, summary, footer total, et l'état d'erreur.
		this._render();
		// Restaure focus + caret à la fin
		const input = this.dom.content.querySelector(`[data-lens-eng-zone-text="${zoneKey}"]`);
		if (input) {
			input.focus();
			const len = input.value.length;
			input.setSelectionRange(len, len);
		}
	},

	/* ====================================================================== */
	/* ORDONNANCE                                                              */
	/* ====================================================================== */

	_renderOrdonnance() {
		this._ensureOrdonnance();
		const ord = this.state.ordonnance;
		const needsAddition = this.state.parcours === 'vision-pres' || this.state.parcours === 'progressifs';
		const showHelp = ord._helpOpen === true;
		const errMsg = this.state.ordonnanceError;
		const ordCfg = this.state.ordSettings || {};

		this.dom.content.innerHTML = `
			<div class="text-l mm-centered">
				<h2 class="text-l">${this._escape(this._t('step.ordonnance.title', 'Indiquer votre correction'))}</h2>
			</div>
			<div class="mm-lt-ordonnance">
				${errMsg ? `<div class="mm-lt-ordonnance-error text-sm" role="alert">${this._escape(errMsg)}</div>` : ''}

				${this._buildOrdonnanceUploadSection(ord, ordCfg)}

				${ordCfg.intermediateText ? `<p class="text-sm mm-black-500 mm-centered">${this._escape(ordCfg.intermediateText)}</p>` : ''}

				${this._buildEyeInputFieldset('d', ord)}
				${this._buildEyeInputFieldset('g', ord)}

				<div class="mm-lt-ord-field">
					<label class="text-sm">${this._escape(this._t('ordonnance.field_ecart', 'Écart pupillaire'))}<span class="mm-lt-required-mark" aria-hidden="true">*</span></label>
					<div class="mm-lt-ord-pair">
						${this._buildInput('ecart_d', ord.ecart_d, this._t('ordonnance.eye_right', 'Œil droit'))}
						${this._buildInput('ecart_g', ord.ecart_g, this._t('ordonnance.eye_left', 'Œil gauche'))}
					</div>
				</div>

				${needsAddition ? `
					<div class="mm-lt-ord-field">
						<label class="text-sm" for="lt-ord-addition">${this._escape(this._t('ordonnance.field_addition', 'Addition (vision de près)'))}<span class="mm-lt-required-mark" aria-hidden="true">*</span></label>
						${this._buildSelect('addition', ord.addition, this._t('ordonnance.field_addition_placeholder', '+0,75 / +4,50'), this._buildRangeOptions(0.75, 4.5, 0.25, 'positive-decimal'), 'lt-ord-addition')}
					</div>
				` : ''}

				<div class="mm-lt-ord-field">
					<div class="mm-lt-ord-label-row">
						<label class="text-sm" for="lt-ord-distance">${this._escape(this._t('ordonnance.field_distance', 'Distance pupillaire'))}</label>
						${ordCfg.pupillaryHelp ? `<button type="button" class="mm-lt-ord-help-link text-sm mm-black-500 mm-underline" data-lens-ord-help-toggle aria-expanded="${showHelp}" aria-controls="lt-ord-help-tooltip">${this._escape(this._t('ordonnance.field_ecart_help', 'Comment le calculer'))}</button>` : ''}
					</div>
					${this._buildInput('distance_p', ord.distance_p, this._t('ordonnance.field_distance_placeholder', 'Sphère'), 'lt-ord-distance')}
					<small class="mm-lt-ord-helper text-sm mm-black-400">25 / 40 mm</small>
					${this.state.fittingboxPd?.apiKey ? `
						<button type="button" class="mm-lt-ord-fb-measure-btn text-sm" data-lens-ord-fb-measure>
							${this._escape(this._t('ordonnance.measure_now', 'Mesurer maintenant avec FittingBox'))}
						</button>
					` : ''}
					${ordCfg.pupillaryHelp ? `
						<div id="lt-ord-help-tooltip" class="mm-lt-ord-tooltip text-xs" data-lens-ord-help role="tooltip" ${showHelp ? '' : 'hidden'}>${this._escape(ordCfg.pupillaryHelp)}</div>
					` : ''}
				</div>

				${this._buildPrescriberSection(ord)}

				${(ordCfg.doubtText || ordCfg.phone) ? `
					<div class="mm-lt-ord-doubt">
						${ordCfg.doubtText ? `<span class="text">${ordCfg.doubtText}</span>` : ''}
						<div class="mm-flex mm-gap-1">
						${ordCfg.doubtText2 ? `<span class="text mm-black-500">${ordCfg.doubtText2}</span>` : ''}
						${ordCfg.phone ? ` <a class="mm-lt-ord-phone text mm-primary" href="tel:${this._escape(ordCfg.phone.replace(/\s+/g, ''))}">${this._escape(ordCfg.phone)}</a>` : ''}
						</div>
					</div>
				` : ''}

				${this.state.ordonnanceSkipEnabled ? `
					<button type="button" class="mm-lt-ordonnance-skip text" data-lens-ord-skip>
						${this._escape(this._t('ordonnance.skip_button', 'Passer cette étape (test)'))}
					</button>
				` : ''}
			</div>
		`;
	},

	_buildInput(field, currentValue, placeholder, id = '') {
		const hasError = this.state.ordonnanceFieldErrors?.has(field);
		return `
			<input ${id ? `id="${id}"` : ''}
				type="text"
				inputmode="decimal"
				class="mm-lt-ord-input text-sm${hasError ? ' mm-lt-ord-input--error' : ''}"
				data-lens-ord-field="${field}"
				value="${this._escape(currentValue || '')}"
				placeholder="${this._escape(placeholder)}"
				${hasError ? 'aria-invalid="true"' : ''}
				aria-label="${this._escape(placeholder)}">
		`;
	},

	/* Génère un tableau d'options [{value,label}] pour un range numérique.
	 * Format français : virgule décimale, signe optionnel.
	 *   - 'signed-decimal'   : -10,00 ... -0,25 / 0,00 / +0,25 ... +10,00 (sphère, cylindre)
	 *   - 'positive-decimal' : +0,75 ... +4,50 (addition, toujours positive)
	 *   - 'integer'          : 0 / 1 / ... / 180 (axe) */
	_buildRangeOptions(min, max, step, format = 'signed-decimal') {
		const opts = [];
		const decimals = step >= 1 ? 0 : 2;
		const stepsCount = Math.round((max - min) / step);
		for (let i = 0; i <= stepsCount; i++) {
			const v = Math.round((min + i * step) * 100) / 100;
			let label;
			if (format === 'integer') {
				label = String(v);
			} else if (format === 'positive-decimal') {
				label = '+' + v.toFixed(decimals).replace('.', ',');
			} else {
				const formatted = v.toFixed(decimals).replace('.', ',');
				label = v > 0 ? '+' + formatted : formatted;
			}
			opts.push({ value: label, label });
		}
		return opts;
	},

	/* Variante <select> de _buildInput pour les champs à valeurs predéfinies (sphère/cylindre/axe/addition).
	 * - L'event 'input' delegué dans _bindEvents s'applique aux <select> depuis HTML5, donc
	 *   data-lens-ord-field continue de declencher _updateOrdonnanceField au change.
	 * - Placeholder = 1ère option vide + disabled pour pousser l'utilisateur à choisir. */
	_buildSelect(field, currentValue, placeholder, options, id = '') {
		const hasError = this.state.ordonnanceFieldErrors?.has(field);
		const optsHtml = options.map(o =>
			`<option value="${this._escape(o.value)}"${currentValue === o.value ? ' selected' : ''}>${this._escape(o.label)}</option>`
		).join('');
		return `
			<select ${id ? `id="${id}"` : ''}
				class="mm-lt-ord-input text-sm${hasError ? ' mm-lt-ord-input--error' : ''}"
				data-lens-ord-field="${field}"
				${hasError ? 'aria-invalid="true"' : ''}
				aria-label="${this._escape(placeholder)}">
				<option value=""${!currentValue ? ' selected' : ''} disabled>${this._escape(placeholder)}</option>
				${optsHtml}
			</select>
		`;
	},

	_buildEyeInputFieldset(eye, ord) {
		const eyeLabel = eye === 'd'
			? this._t('ordonnance.eye_right', 'Œil Droit')
			: this._t('ordonnance.eye_left', 'Œil Gauche');
		/* Ranges figés (cf. brief Céline 2026-06) :
		 *   Sphère  : -10,00 → +10,00 par 0,25
		 *   Cylindre: -6,00 → +6,00 par 0,25
		 *   Axe     : 0 → 180 par 1 */
		const sphereOpts   = this._buildRangeOptions(-10, 10, 0.25, 'signed-decimal');
		const cylindreOpts = this._buildRangeOptions(-6, 6, 0.25, 'signed-decimal');
		const axeOpts      = this._buildRangeOptions(0, 180, 1, 'integer');
		return `
			<div class="mm-lt-ord-eye">
				<legend class="text-sm">${this._escape(eyeLabel)}</legend>
				<div class="mm-lt-ord-eye-grid">
					${this._buildSelect('sphere_' + eye, ord['sphere_' + eye], this._t('ordonnance.field_sphere', 'Sphère'), sphereOpts)}
					${this._buildSelect('cylindre_' + eye, ord['cylindre_' + eye], this._t('ordonnance.field_cylindre', 'Cylindre'), cylindreOpts)}
					${this._buildSelect('axe_' + eye, ord['axe_' + eye], this._t('ordonnance.field_axe', 'Axe'), axeOpts)}
				</div>
			</div>
		`;
	},

	/* Section "Prescripteur" : nom + date d'ordonnance obligatoires (mentions legales devis),
	 * + N° Securite Sociale optionnel. Affichee dans tous les cas (fichier joint ou formulaire manuel). */
	_buildPrescriberSection(ord) {
		const nameLabel = this._escape(this._t('ordonnance.prescriber_name', 'Nom du prescripteur'));
		const dateLabel = this._escape(this._t('ordonnance.prescription_date', 'Date de l\'ordonnance'));
		const nssLabel  = this._escape(this._t('ordonnance.social_security', 'N° Sécurité Sociale (optionnel)'));
		return `
			<fieldset class="mm-lt-ord-prescriber">
				<legend class="text-sm">${this._escape(this._t('ordonnance.prescriber_section', 'Informations prescripteur'))}</legend>
				<div class="mm-lt-ord-field">
					<label class="text-sm" for="lt-ord-prescriber-name">${nameLabel}</label>
					<input id="lt-ord-prescriber-name"
						type="text"
						class="mm-lt-ord-input text-sm"
						data-lens-ord-field="prescriber_name"
						value="${this._escape(ord.prescriber_name || '')}"
						placeholder="${this._escape(this._t('ordonnance.prescriber_name_placeholder', 'Dr Dupont'))}"
						aria-label="${nameLabel}">
				</div>
				<div class="mm-lt-ord-field">
					<label class="text-sm" for="lt-ord-prescription-date">${dateLabel}<span class="mm-lt-required-mark" aria-hidden="true">*</span></label>
					<input id="lt-ord-prescription-date"
						type="date"
						class="mm-lt-ord-input text-sm${this.state.ordonnanceFieldErrors?.has('prescription_date') ? ' mm-lt-ord-input--error' : ''}"
						data-lens-ord-field="prescription_date"
						value="${this._escape(ord.prescription_date || '')}"
						${this.state.ordonnanceFieldErrors?.has('prescription_date') ? 'aria-invalid="true"' : ''}
						aria-label="${dateLabel}">
				</div>
				<div class="mm-lt-ord-field">
					<label class="text-sm" for="lt-ord-nss">${nssLabel}</label>
					<input id="lt-ord-nss"
						type="text"
						inputmode="numeric"
						class="mm-lt-ord-input text-sm"
						data-lens-ord-field="social_security_number"
						value="${this._escape(ord.social_security_number || '')}"
						placeholder="1 99 99 99 999 999 99"
						aria-label="${nssLabel}">
				</div>
			</fieldset>
		`;
	},

	_buildOrdonnanceUploadSection(ord, cfg) {
		const file = ord.file;
		const iconCamera = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`;
		const iconUpload = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>`;

		/* Champ email obligatoire si fichier upload (HDS) — pre-rempli depuis customer si logged-in */
		const currentEmail = this._escape(ord.email || this.state.identity.email || '');
		const hasEmailError = !!this.state.ordonnanceEmailError;
		const emailInputClass = `mm-lt-ord-email-input text-sm${hasEmailError ? ' mm-lt-ord-email-input--error' : ''}`;
		const emailFieldHtml = `
			<div class="mm-lt-ord-email-field">
				<label class="mm-lt-ord-email-label text-sm" for="mm-lt-ord-email">
					${this._escape(this._t('ordonnance.email_label', 'Votre email'))}
				</label>
				<input type="email"
					id="mm-lt-ord-email"
					class="${emailInputClass}"
					data-lens-ord-email
					value="${currentEmail}"
					autocomplete="email"
					${hasEmailError ? 'aria-invalid="true"' : ''}
					placeholder="${this._escape(this._t('ordonnance.email_placeholder', 'votre@email.com'))}">
				<p class="mm-lt-ord-email-hds text-xs mm-black-500">
					${this._escape(this._t('ordonnance.email_hds_info', "Nous avons besoin de votre email pour enregistrer votre ordonnance. Notre hébergement est certifié HDS, garantissant la sécurité de vos données."))}
				</p>
			</div>
		`;

		const previewOrButtons = file ? `
			<div class="mm-lt-upload-preview">
				<span class="text-sm">${this._escape(this._t('ordonnance.upload_selected', 'Fichier joint').replace('{{ name }}', file.name).replace('{{ size }}', (file.size / 1024).toFixed(0) + ' Ko'))}</span>
				<button type="button" class="mm-lt-upload-remove text-sm" data-lens-ord-file-remove>${this._escape(this._t('ordonnance.upload_remove', 'Retirer'))}</button>
			</div>
		` : `
			<div class="mm-lt-ord-upload-buttons">
				<label class="mm-lt-ord-upload-card mm-lt-ord-upload-card--mobile-only">
					<span class="mm-lt-ord-upload-card-svg">
						${iconCamera}
					</span>
					<span class="text-sm">${this._escape(this._t('ordonnance.upload_capture', 'Prendre en photo'))}</span>
					<input type="file" class="mm-lt-upload-input" data-lens-ord-file accept="image/*" capture="environment">
				</label>
				<label class="mm-lt-ord-upload-card">
					<span class="mm-lt-ord-upload-card-svg">
						${iconUpload}
					</span>
					<span class="text-sm">${this._escape(this._t('ordonnance.upload_label', 'Ajouter un fichier'))}</span>
					<input type="file" class="mm-lt-upload-input" data-lens-ord-file accept="application/pdf,image/jpeg,image/png">
				</label>
			</div>
		`;

		/* L'erreur d'email vit dans state.ordonnanceEmailError (set au moment du goNext) */
		const emailError = this.state.ordonnanceEmailError ? `<p class="mm-lt-ord-email-error text-xs" role="alert">${this._escape(this.state.ordonnanceEmailError)}</p>` : '';

		return `
			<section class="mm-lt-ord-upload-section">
				<div class="mm-flex mm-column">
					${cfg.uploadTitle ? `<h3 class="text-sm">${this._escape(cfg.uploadTitle)}</h3>` : ''}
					${cfg.uploadSubtitle ? `<p class="text-sm mm-black-500">${this._escape(cfg.uploadSubtitle)}</p>` : ''}
				</div>
				${previewOrButtons}
				${file ? emailFieldHtml + emailError : ''}
				${cfg.uploadFooter ? `<p class="mm-lt-ord-upload-footer text-xs mm-black-400">${this._escape(cfg.uploadFooter)}</p>` : ''}
			</section>
		`;
	},

	_ensureOrdonnance() {
		if (!this.state.ordonnance) {
			this.state.ordonnance = {
				sphere_d: '', cylindre_d: '', axe_d: '',
				sphere_g: '', cylindre_g: '', axe_g: '',
				ecart_d: '', ecart_g: '',
				distance_p: '',
				addition: '',
				file: null, skipped: false,
				email: this.state.identity?.email || '',
				/* Champs prescripteur (obligatoires sur devis legal — cf. brief Celine).
				 * social_security est optionnel ("bonus ultime" dans le brief). */
				prescriber_name:        '',
				prescription_date:      '',
				social_security_number: '',
				_helpOpen: false,
			};
		}
	},

	_updateOrdonnanceField(field, value) {
		this._ensureOrdonnance();
		this.state.ordonnance[field] = value;
	},

	_toggleOrdonnanceHelp() {
		this._ensureOrdonnance();
		this.state.ordonnance._helpOpen = !this.state.ordonnance._helpOpen;
		// Toggle DOM directement pour éviter re-render et perte de focus
		const tip = this.dom.content.querySelector('[data-lens-ord-help]');
		const btn = this.dom.content.querySelector('[data-lens-ord-help-toggle]');
		if (tip) tip.hidden = !this.state.ordonnance._helpOpen;
		if (btn) btn.setAttribute('aria-expanded', String(this.state.ordonnance._helpOpen));
	},

	async _handleOrdonnanceFile(input) {
		const file = input.files && input.files[0];
		if (!file) return;
		/* Limite alignée sur Optimum E6 (250 Ko max pour les ordonnances).
		 * Pas de compression auto côté front : on demande au client de scanner plus léger. */
		const maxBytes = 250 * 1024;
		const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
		if (!allowed.includes(file.type)) {
			this.state.ordonnanceError = this._t('errors.file_type', 'Format non supporté');
			/* Reset l'input pour permettre de re-sélectionner le même fichier après correction. */
			input.value = '';
			this._render();
			return;
		}
		if (file.size > maxBytes) {
			this.state.ordonnanceError = this._t('errors.file_too_large', 'Fichier trop volumineux');
			input.value = '';
			this._render();
			return;
		}
		/* Validation contenu (magic number) : evite que Optimum rejette avec
		 * ImagickException plus tard. Cas typiques attrapes ici :
		 * - .pdf renomme depuis un .heic (iPhone) mal converti
		 * - .jpg corrompu / partiellement telecharge
		 * - fichier vide (0 octet passe la limite mais Optimum plante) */
		const contentValid = await this._verifyFileHeader(file);
		if (!contentValid) {
			this.state.ordonnanceError = this._t('errors.file_corrupted', 'Fichier illisible. Merci de re-scanner ou choisir un autre fichier.');
			input.value = '';
			this._render();
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			this._ensureOrdonnance();
			this.state.ordonnance.file = {
				name: file.name,
				size: file.size,
				type: file.type,
				dataUrl: reader.result,
			};
			this.state.ordonnanceError = null;
			this._render();
		};
		reader.readAsDataURL(file);
	},

	/* Verifie le magic number du fichier (bytes 0-3) plutot que se fier au MIME
	 * annonce par le browser (facilement falsifiable). Types acceptes:
	 *   PDF: 25 50 44 46 ('%PDF')
	 *   JPG: FF D8 FF
	 *   PNG: 89 50 4E 47
	 * Retourne une Promise<boolean>. */
	_verifyFileHeader(file) {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = () => {
				try {
					const bytes = new Uint8Array(reader.result);
					if (bytes.length < 4) return resolve(false);
					const hex = Array.from(bytes.slice(0, 4), b => b.toString(16).padStart(2, '0')).join('');
					const isPdf = hex.startsWith('25504446');
					const isJpg = hex.startsWith('ffd8ff');
					const isPng = hex.startsWith('89504e47');
					resolve(isPdf || isJpg || isPng);
				} catch (_) {
					resolve(false);
				}
			};
			reader.onerror = () => resolve(false);
			reader.readAsArrayBuffer(file.slice(0, 4));
		});
	},

	_handleOrdonnanceFileRemove() {
		if (this.state.ordonnance) this.state.ordonnance.file = null;
		this._render();
	},

	_handleOrdonnanceSkip() {
		this.state.ordonnance = { skipped: true };
		this.state.ordonnanceError = null;
		this.goNext();
	},

	_validateOrdonnance() {
		// Si skippée (mode test), on valide.
		if (this.state.ordonnance?.skipped) {
			this.state.ordonnanceError = null;
			this.state.ordonnanceFieldErrors = new Set();
			return true;
		}
		this._ensureOrdonnance();
		const ord = this.state.ordonnance;

		/* Collecte TOUS les champs manquants en un seul passage pour que la border rouge
		 * s'affiche sur l'ensemble des erreurs d'un coup — evite "fix un champ, re-click, voir le suivant". */
		const missing = new Set();

		/* Date d'ordonnance : obligatoire (legal). Le nom du prescripteur reste OPTIONNEL
		 * — cf. brief Celine 2026-08, certains clients n'ont pas le nom sous la main. */
		if (!String(ord.prescription_date || '').trim()) missing.add('prescription_date');

		/* Branche FICHIER : si une ordonnance est jointe, on ne demande PAS les champs manuels
		 * (l'opticien lira les valeurs dans le PDF). Seul l'email est obligatoire pour le lien HDS Optimum. */
		if (ord.file) {
			const emailInvalid = !this._isValidEmail(ord.email);
			this.state.ordonnanceEmailError = emailInvalid
				? this._t('errors.email_required_for_upload', 'Email requis pour enregistrer votre ordonnance')
				: null;
			if (missing.size || emailInvalid) {
				this.state.ordonnanceFieldErrors = missing;
				this.state.ordonnanceError = missing.has('prescription_date')
					? this._t('errors.prescription_date_required', 'La date d\'ordonnance est obligatoire.')
					: null;
				this._render();
				return false;
			}
			this.state.ordonnanceFieldErrors = new Set();
			this.state.ordonnanceError = null;
			return true;
		}

		/* Branche FORMULAIRE MANUEL : pas de fichier → on exige sphere/cylindre/axe + ecart
		 * (+ addition selon parcours). Cylindre et axe passes obligatoires suite au brief
		 * Celine 2026-10 : l'opticien ne peut pas commander les verres sans ces infos. */
		const needsAddition = this.state.parcours === 'vision-pres' || this.state.parcours === 'progressifs';
		const required = ['sphere_d', 'cylindre_d', 'axe_d', 'sphere_g', 'cylindre_g', 'axe_g', 'ecart_d', 'ecart_g'];
		if (needsAddition) required.push('addition');
		for (const f of required) {
			if (!String(ord[f] || '').trim()) missing.add(f);
		}

		if (missing.size) {
			this.state.ordonnanceFieldErrors = missing;
			this.state.ordonnanceError = missing.has('prescription_date') && missing.size === 1
				? this._t('errors.prescription_date_required', 'La date d\'ordonnance est obligatoire.')
				: this._t('errors.ordonnance_required', 'Champs obligatoires manquants');
			this._render();
			return false;
		}
		this.state.ordonnanceFieldErrors = new Set();
		this.state.ordonnanceError = null;
		this.state.ordonnanceEmailError = null;
		return true;
	},

	_isValidEmail(email) {
		return typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	},

	/* Upload ordonnance vers Optimum via la GCF middleware.
	 * - GCF configurée : POST réel + stockage clientId/visiteId pour réutilisation au devis.
	 * - GCF absente    : DRY-RUN — log dans la console + simulation de réponse pour valider le flow. */
	async _uploadOrdoToOptimum() {
		const ord = this.state.ordonnance;
		const opt = this.state.optimum;
		if (!ord?.file?.dataUrl) return;

		/* dataUrl format: "data:application/pdf;base64,JVBERi0xLjQK..." → on garde juste le base64 apres la virgule */
		const base64 = ord.file.dataUrl.split(',')[1] || '';
		const fileType = (ord.file.type || '').includes('pdf') ? 'pdf' : ((ord.file.type || '').includes('png') ? 'png' : 'jpg');

		const body = {
			action: 'upload_ordo',
			email: ord.email.trim(),
			firstName: this.state.identity.firstName || '',
			lastName: this.state.identity.lastName || '',
			socialSecurityNumber: (ord.social_security_number || '').trim(),
			fileBase64: base64,
			fileType,
			fileName: ord.file.name,
			optimum_client_id: opt.clientId || null,
			visite_id: opt.visiteId || null,
		};

		if (!opt.gcfUrl) {
			this._logOptimumDryRun('upload_ordo', body);
			opt.clientId = opt.clientId || `DRYRUN_CLIENT_${Date.now().toString(36).toUpperCase()}`;
			opt.visiteId = opt.visiteId || `DRYRUN_VISITE_${Date.now().toString(36).toUpperCase()}`;
			opt.ordoUploaded = true;
			return;
		}

		const res = await fetch(opt.gcfUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Theme-Secret': opt.themeSecret,
			},
			body: JSON.stringify(body),
		});
		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			throw new Error(data.error || `HTTP ${res.status}`);
		}
		const data = await res.json();
		opt.clientId = data.optimum_client_id;
		opt.visiteId = data.visite_id;
		opt.ordoUploaded = true;
		window.log('info', '🔭 [LENS_TUNNEL]', 'Optimum ordo uploaded', { clientId: data.optimum_client_id, visiteId: data.visite_id });
	},

	/* ====================================================================== */
	/* FITTINGBOX PD MEASUREMENT (mesure ecart pupillaire via webcam)          */
	/* Doc : https://www.fittingbox.com/en/resources/help-center/pd-measurement */
	/* ====================================================================== */

	/* Lazy-load du script onsite FittingBox. Promise partagee pour gerer les
	 * clics rapides (evite de charger le script 2 fois). */
	_loadFittingboxScript() {
		const fb = this.state.fittingboxPd;
		if (fb.scriptLoaded || typeof window.Msrt !== 'undefined') {
			fb.scriptLoaded = true;
			return Promise.resolve();
		}
		if (fb.scriptLoading) return fb.scriptLoading;
		fb.scriptLoading = new Promise((resolve, reject) => {
			const s = document.createElement('script');
			s.src = 'https://msrt-integration-api.fittingbox.com/index.js';
			s.async = true;
			s.onload  = () => { fb.scriptLoaded = true; resolve(); };
			s.onerror = () => reject(new Error('FittingBox script load failed'));
			document.head.appendChild(s);
		});
		return fb.scriptLoading;
	},

	/* Cree (1 fois) les containers DOM requis par le widget FittingBox.
	 * On les met dans document.body pour eviter le pointer-events:none du
	 * wrapper tunnel. Retourne { overlay, msrtDiv }. */
	_ensureFittingboxContainers() {
		let overlay = document.getElementById('mm-fb-pd-overlay');
		let msrtDiv = document.getElementById('mm-fb-pd-wrapper');
		if (!overlay) {
			overlay = document.createElement('div');
			overlay.id = 'mm-fb-pd-overlay';
			overlay.className = 'mm-fb-pd-overlay';
			overlay.style.display = 'none';
			document.body.appendChild(overlay);
		}
		if (!msrtDiv) {
			msrtDiv = document.createElement('div');
			msrtDiv.id = 'mm-fb-pd-wrapper';
			msrtDiv.className = 'mm-fb-pd-wrapper';
			msrtDiv.style.display = 'none';
			const inner = document.createElement('div');
			inner.id = 'msrt-container';
			msrtDiv.appendChild(inner);
			document.body.appendChild(msrtDiv);
		}
		return { overlay, msrtDiv };
	},

	/* Ouvre le widget FittingBox PD Measurement. Le resultat est passe a
	 * onGetResultPd ; la structure exacte (`pd`, `pdLeft`, `pdRight`,
	 * `nearVision`, etc.) n'est pas documentee — on log brut et on injecte
	 * dans state.ordonnance.ecart_d/ecart_g les valeurs trouvees. */
	async _launchFittingboxPd() {
		const fb = this.state.fittingboxPd;
		if (!fb.apiKey) {
			window.log('warn', '🔭 [LENS_TUNNEL]', 'FittingBox PD: apiKey manquante');
			return;
		}
		try {
			await this._loadFittingboxScript();
		} catch (err) {
			window.log('error', '🔭 [LENS_TUNNEL]', 'FittingBox script failed', { error: err.message });
			return;
		}
		const { overlay, msrtDiv } = this._ensureFittingboxContainers();
		overlay.style.display = 'block';
		msrtDiv.style.display = 'block';
		window.Msrt.createWidget('msrt-container', {
			apiKey:    fb.apiKey,
			widgetUrl: 'https://pd-measurement.fittingbox.com',
			onGetResultPd: (result) => {
				/* Log brut pour decouvrir la structure exacte (a ajuster si besoin) */
				window.log('info', '🔭 [LENS_TUNNEL]', 'FittingBox PD result', result);
				this._applyFittingboxPdResult(result);
			},
			onTooManyErrors: () => {
				window.log('warn', '🔭 [LENS_TUNNEL]', 'FittingBox PD: too many errors');
			},
			onClose: () => {
				overlay.style.display = 'none';
				msrtDiv.style.display = 'none';
			},
		});
	},

	/* Injecte le resultat PD dans state.ordonnance et re-render.
	 * Heuristique sur les champs courants (pdRight/pdLeft ou right/left ou r/l).
	 * Le total `pd` global est mappe en distance_p si fourni. */
	_applyFittingboxPdResult(result) {
		if (!result || typeof result !== 'object') return;
		this._ensureOrdonnance();
		const ord = this.state.ordonnance;
		const right = result.pdRight ?? result.right ?? result.r ?? result.rightPd ?? null;
		const left  = result.pdLeft  ?? result.left  ?? result.l ?? result.leftPd  ?? null;
		const total = result.pd      ?? result.totalPd ?? result.total ?? null;
		if (right != null) ord.ecart_d = String(right);
		if (left  != null) ord.ecart_g = String(left);
		if (total != null) ord.distance_p = String(total);
		this._render();
	},

	/* Log lisible du payload qui aurait été envoyé à la GCF Optimum. Tronque fileBase64
	 * (taille en Ko approximative) pour rester lisible dans la console. */
	_logOptimumDryRun(action, body) {
		const preview = { ...body };
		if (preview.fileBase64) {
			const approxKb = Math.round((preview.fileBase64.length * 0.75) / 1024);
			preview.fileBase64 = `[base64 tronqué — ~${approxKb} Ko, ${preview.fileBase64.length} chars]`;
		}
		try { console.groupCollapsed(`🔭 [OPTIMUM DRY-RUN] ${action} — GCF non configurée, simulation`); } catch { console.log(`🔭 [OPTIMUM DRY-RUN] ${action} — GCF non configurée`); }
		console.log('Payload qui serait envoyé à la GCF :', preview);
		console.log('Pour activer l\'envoi réel : Customizer → MM-Lens-Tunnel → Intégration Optimum (HDS)');
		try { console.groupEnd(); } catch { /* noop */ }
	},

	/* ====================================================================== */
	/* RECAP                                                                   */
	/* ====================================================================== */

	_renderRecap() {
		const final = this._getFinalCombination();
		const cfg = this.state.recapSettings || {};
		const ordCfg = this.state.ordSettings || {};

		if (!final) {
			this.dom.content.innerHTML = `
				<div class="mm-lt-error" role="alert">${this._escape(this._t('errors.no_combination', 'Aucune combinaison disponible'))}</div>
			`;
			return;
		}

		const framePrice = this.state.productPrice ? this.state.productPrice / 100 : 0;
		const grandTotal = this._getGrandTotal();

		const rowSimple = (label, value, subPrice) => `
			<div class="mm-lt-recap-row">
				<span class="mm-lt-recap-row-label text-sm mm-black-500">${this._escape(label)}</span>
				<span class="mm-lt-recap-row-value text-sm">
					${this._escape(value)}
					${subPrice && subPrice > 0 ? `<span class="mm-lt-recap-row-subprice">+${this._formatPrice(subPrice).replace('€', '')}€</span>` : ''}
				</span>
			</div>
		`;

		const lensRows = [];

		// Couleur (avec avatar + sous-libellé "GAMME [catégorie]" + prix à droite)
		if (final.couleur) {
			const colorMeta = this._getOptionMeta(final.couleur) || {};
			const colorLabel = this._label(final.couleur);
			const avatar = colorMeta.image
				? `<img src="${this._escape(colorMeta.image)}" alt="${this._escape(colorMeta.image_alt || colorLabel)}" loading="lazy">`
				: `<span class="mm-lt-recap-color-swatch" style="background:${this._swatchColor(final.couleur.id)};" aria-hidden="true"></span>`;
			// Sub-label = "GAMME <catégorie>" (remplace l'ancienne row catégorie séparée)
			let subLabel = '';
			if (final.categorie) {
				const catLabel = this._label(final.categorie);
				const pattern = this._t('recap.range_pattern', 'Gamme %s');
				subLabel = pattern.replace('%s', catLabel);
			} else {
				subLabel = colorMeta.description || '';
			}
			// Le prix affiché à droite cumule couleur + catégorie + sous-catégorie
			// (ex: Fun=0 + Uni=10 + Bleu=0 → +10€).
			const totalColorPrice = (final.couleur.price || 0)
				+ (final.categorie?.price || 0)
				+ (final.sous_categorie?.price || 0);
			lensRows.push(`
				<div class="mm-lt-recap-row mm-lt-recap-row--color">
					<span class="mm-lt-recap-color">
						<span class="mm-lt-recap-color-avatar">${avatar}</span>
						<span class="mm-lt-recap-color-text">
							<span class="text-sm">${this._escape(colorLabel)}</span>
							${subLabel ? `<span class="text-sm mm-upcase mm-black-500">${this._escape(subLabel)}</span>` : ''}
						</span>
					</span>
					${totalColorPrice > 0 ? `<span class="text-sm mm-black-500">+${this._formatPrice(totalColorPrice)}</span>` : ''}
				</div>
			`);
		}

		// Type de vision (parcours)
		const parcoursOpt = this.data.parcours.find(p => p.id === this.state.parcours);
		if (parcoursOpt) {
			lensRows.push(rowSimple(this._t('recap.vision_type_label', 'Type de vision'), this._label(parcoursOpt)));
		}

		// Ordonnance (multi-ligne par œil)
		const ord = this.state.ordonnance;
		if (ord && !ord.skipped && (ord.sphere_d || ord.sphere_g)) {
			const eyeR = this._formatEyeShort(ord, 'd');
			const eyeL = this._formatEyeShort(ord, 'g');
			lensRows.push(`
				<div class="mm-lt-recap-row">
					<span class="mm-lt-recap-row-label text-sm mm-black-500">${this._escape(this._t('recap.ordonnance_label', 'Correction'))}</span>
					<span class="mm-lt-recap-row-value mm-lt-recap-row-value--multiline text-sm">
						<span>${this._escape(this._t('ordonnance.eye_right', 'Œil droit'))} : <span class="mm-black-500">${eyeR}</span></span>
						<span>${this._escape(this._t('ordonnance.eye_left', 'Œil gauche'))} : <span class="mm-black-500">${eyeL}</span></span>
					</span>
				</div>
			`);
		}

		// (Ligne "Catégorie" retirée : la catégorie est désormais affichée en sub-label de la couleur ci-dessus)
		// Famille de verres (Transparents +39€ / Solaires +49€ / Teintes au soleil +109€)
		if (final.type_verre) lensRows.push(rowSimple(this._t('step.type_verre.title', 'Famille de verres'), this._label(final.type_verre), final.type_verre.price));
		// Gamme progressifs (Classique / Évolué / Luxe) — uniquement pour le parcours progressifs
		if (final.gamme_prog) lensRows.push(rowSimple(this._t('step.gamme_prog.title', 'Gamme progressifs'), this._label(final.gamme_prog), final.gamme_prog.price));
		// Amincissement
		if (final.taille_verre) lensRows.push(rowSimple(this._t('step.taille_verre.title', 'Amincissement'), this._label(final.taille_verre), final.taille_verre.price));
		// Anti-reflet
		if (final.anti_reflet) lensRows.push(rowSimple(this._t('step.anti_reflet.title', 'Anti-reflet'), this._label(final.anti_reflet), final.anti_reflet.price));
		// Cat solaire
		if (final.cat_solaire) lensRows.push(rowSimple(this._t('step.cat_solaire.title', 'Intensité'), this._label(final.cat_solaire), final.cat_solaire.price));

		// Art du détail (option choisie en amont) + gravure éventuelle
		const artOpt = this.state.artDetail
			? (this.state.artDetailOptions || []).find(o => o.id === this.state.artDetail)
			: null;
		if (artOpt) {
			// Si l'art_detail mène à la gravure ET qu'au moins une zone est activée,
			// on affiche le total cumulé. Sinon on affiche juste le prix_eur fixe (ou fallback texte).
			const activeZones = this._getActiveZoneCount();
			if (activeZones > 0) {
				const displayLabel = this._getEngravingDisplayLabel(artOpt);
				const detail = `${this._escape(displayLabel)} (${activeZones} ${activeZones > 1 ? 'gravures' : 'gravure'})`;
				lensRows.push(rowSimple(this._t('recap.engraving_detail_label', 'Art du détail'), detail, this._getEngravingTotal()));
				// Lignes par zone gravée
				const zones = ['lt', 'lb', 'rt', 'rb'];
				const eng = this.state.engraving;
				const engravedZones = zones
					.filter(k => this._isZoneEngraved(eng.zones[k]))
					.map(k => {
						const zoneLabel = this._t(`engraving.zone_${k}`, k);
						return `<span>${this._escape(zoneLabel)} : <span class="mm-black-500">"${this._escape(eng.zones[k].text)}"</span></span>`;
					});
				if (engravedZones.length) {
					lensRows.push(`
						<div class="mm-lt-recap-row">
							<span class="mm-lt-recap-row-label text-sm mm-black-500">${this._escape(this._t('recap.engraving_label', 'Gravures'))}</span>
							<span class="mm-lt-recap-row-value mm-lt-recap-row-value--multiline text-sm">
								${engravedZones.join('')}
							</span>
						</div>
					`);
				}
			} else {
				// Art du détail sans gravure (Aucune finition, Insertion diamant sur devis, etc.)
				// Affiche le label + prix fixe ou texte fallback
				const fixedPrice = (typeof artOpt.price_eur === 'number' && artOpt.price_eur > 0) ? artOpt.price_eur : 0;
				const priceValue = fixedPrice > 0 ? fixedPrice : null;
				const row = priceValue !== null
					? rowSimple(this._t('recap.engraving_detail_label', 'Art du détail'), artOpt.label, priceValue)
					: `
						<div class="mm-lt-recap-row">
							<span class="mm-lt-recap-row-label text-sm mm-black-500">${this._escape(this._t('recap.engraving_detail_label', 'Art du détail'))}</span>
							<span class="mm-lt-recap-row-value text-sm">
								${this._escape(artOpt.label)}
								${artOpt.price_text_fallback ? `<span class="mm-lt-recap-row-subprice">${this._escape(artOpt.price_text_fallback)}</span>` : ''}
							</span>
						</div>
					`;
				lensRows.push(row);
			}
		} else if (this._hasAnyEngravingZone()) {
			// Legacy : pas d'art_detail mais gravure activée (cas configuration sans blocks)
			const eng = this.state.engraving;
			const colorLabel = this._t(`engraving.color_${eng.color}`, eng.color);
			const lensCount = this._getEngravedLensCount();
			const detail = `${colorLabel} (${lensCount} ${lensCount > 1 ? 'verres' : 'verre'})`;
			lensRows.push(rowSimple(this._t('recap.engraving_detail_label', 'Art du détail'), detail, this._getEngravingTotal()));
		}

		// Total
		lensRows.push(`
			<div class="mm-lt-recap-row mm-lt-recap-row--total">
				<span class="mm-lt-recap-row-label text-sm mm-black-500">${this._escape(this._t('recap.total_label', 'Total'))}</span>
				<span class="mm-lt-recap-row-value mm-lt-recap-total-value text">${this._formatPrice(grandTotal)}</span>
			</div>
		`);

		// Devis : masque le bloc si parcours == sans-correction (pas de verres correctifs
		// -> le devis n'a pas d'utilite, il sert principalement a etre envoye a la mutuelle).
		const showDevis = cfg.devisLabel && cfg.devisAction && this.state.parcours !== 'sans-correction';
		const devisHTML = showDevis ? `
			<div class="mm-lt-recap-row mm-lt-recap-row--action">
				<span class="mm-lt-recap-row-label text-sm mm-black-500">${this._escape(cfg.devisLabel)}</span>
				<button type="button" class="mm-btn mm-btn-secondary text-sm" data-lens-tunnel-quote aria-label="${this._escape(cfg.devisAction)}">
					${this._escape(cfg.devisAction)}
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
				</button>
			</div>
		` : '';

		// Bloc 2e paire
		const secondPair = cfg.secondPairEnabled && (cfg.secondPairMain || cfg.secondPairAlt) ? `
			<div class="mm-lt-recap-secondpair">
				${cfg.secondPairTitle ? `<h4 class="mm-lt-recap-secondpair-title text">${this._escape(cfg.secondPairTitle)}</h4>` : ''}
				<div class="mm-lt-recap-secondpair-content">
					${cfg.secondPairMain ? `<p class="mm-lt-recap-secondpair-deal text-l">${this._escape(cfg.secondPairMain)}</p>` : ''}
					${cfg.secondPairMainSub ? `<p class="mm-lt-recap-secondpair-sub text-sm mm-black-500">${this._escape(cfg.secondPairMainSub)}</p>` : ''}
					${cfg.secondPairSep ? `<p class="mm-lt-recap-secondpair-sep text-sm mm-black-500">${this._escape(cfg.secondPairSep)}</p>` : ''}
					${cfg.secondPairAlt ? `<p class="mm-lt-recap-secondpair-deal text-l">${this._escape(cfg.secondPairAlt)}</p>` : ''}
					${cfg.secondPairAltSub ? `<p class="mm-lt-recap-secondpair-sub text-sm mm-black-500">${this._escape(cfg.secondPairAltSub)}</p>` : ''}
				</div>
			</div>
		` : '';

		// Bloc doute (réutilise ordSettings) : split du doubtText sur "?" pour
		// obtenir titre + sous-texte, conforme aux maquettes (titre noir, sous-texte gris).
		let doubtTitle = '';
		let doubtSubtitle = '';
		if (ordCfg.doubtText) {
			const idx = ordCfg.doubtText.indexOf('?');
			if (idx >= 0) {
				doubtTitle = ordCfg.doubtText.slice(0, idx + 1).trim();
				doubtSubtitle = ordCfg.doubtText.slice(idx + 1).trim();
			} else {
				doubtTitle = ordCfg.doubtText.trim();
			}
		}
		const doubt = (doubtTitle || doubtSubtitle || ordCfg.phone) ? `
			<div class="mm-lt-recap-doubt">
				${doubtTitle ? `<p class="mm-lt-recap-doubt-title text">${this._escape(doubtTitle)}</p>` : ''}
				${(doubtSubtitle || ordCfg.phone) ? `
					<p class="mm-lt-recap-doubt-sub text-sm mm-black-500">
						${doubtSubtitle ? `<span>${this._escape(doubtSubtitle)}</span> ` : ''}
						<div class="mm-flex mm-gap-1">
						${ordCfg.doubtText2 ? `<span class="text mm-black-500">${ordCfg.doubtText2}</span>` : ''}
						${ordCfg.phone ? `<a class="mm-lt-ord-phone text mm-primary" href="tel:${this._escape(ordCfg.phone.replace(/\s+/g, ''))}">${this._escape(ordCfg.phone)}</a>` : ''}
						</div>
					</p>
				` : ''}
			</div>
		` : '';

		this.dom.content.innerHTML = `
			<div class="mm-lt-recap-header">
				<h2 class="text-l mm-centered">${this._escape(cfg.title || this._t('step.recap.title', 'Récapitulatif'))}</h2>
				${cfg.subtitle ? `<p class="text-sm mm-black-500 mm-centered">${this._escape(cfg.subtitle)}</p>` : ''}
				${cfg.leadTime ? `<p class="text-sm mm-black-500 mm-centered">${this._escape(cfg.leadTime)}</p>` : ''}
			</div>
			<div class="mm-lt-recap">
				${this.state.productTitle ? `
					<div class="mm-lt-recap-frame">
						<span class="mm-lt-recap-frame-title text-sm">
							${this._escape(this.state.productTitle)}${this.state.montureVariantTitle ? ` <span class="mm-black-500">· ${this._escape(this.state.montureVariantTitle)}</span>` : ''}
						</span>
						<span class="mm-lt-recap-frame-price text-sm">${this._formatPrice(framePrice)}</span>
					</div>
				` : ''}
				<div class="mm-lt-recap-section">
					${cfg.lensSectionLabel ? `<h3 class="mm-lt-recap-section-title text-sm mm-black-500">${this._escape(cfg.lensSectionLabel)}</h3>` : ''}
					${lensRows.join('')}
				</div>
				${devisHTML}
				${secondPair}
				${doubt}
			</div>
		`;
	},

	_formatEyeShort(ord, eye) {
		const sph = this._escape(ord['sphere_' + eye] || '—');
		const cyl = this._escape(ord['cylindre_' + eye] || '');
		const axe = this._escape(ord['axe_' + eye] || '');
		const sphLbl = this._t('recap.sphere_short', 'Sph.');
		const cylLbl = this._t('recap.cylindre_short', 'Cyl.');
		const axeLbl = this._t('recap.axe_short', 'Axe');
		let out = `${sphLbl} ${sph}`;
		if (cyl) out += ` / ${cylLbl} ${cyl}`;
		if (axe) out += ` / ${axeLbl} ${axe}°`;
		return out;
	},

	/* ====================================================================== */
	/* FOOTER                                                                  */
	/* ====================================================================== */

	_updateFooter(stepKey) {
		if (!this.dom.footer) return;
		// CTA présent UNIQUEMENT sur les étapes où la maquette le prévoit.
		// Les étapes "options simples" (correction, type_verre, taille_verre, sous_categorie,
		// categorie, couleur, cat_solaire, anti_reflet) avancent au clic sur l'option choisie.
		const stepsWithCta = ['ordonnance', 'combo', 'gravure', 'recap'];

		let actionBtn = '';
		if (stepKey === 'recap') {
			const final = this._getFinalCombination();
			actionBtn = `<button type="button" class="mm-btn mm-btn-secondary mm-lt-footer-cta mm-overtitre-xlarge" data-lens-tunnel-validate ${!final ? 'disabled' : ''} data-button-animate-chars>${this._escape(this._t('nav.validate', 'Ajouter au panier'))}</button>`;
		} else if (stepKey === 'gravure') {
			// Label spécifique "Terminer ma configuration" + disabled si une zone activée est invalide.
			const disabled = this._hasInvalidEngravingZone();
			actionBtn = `<button type="button" class="mm-btn mm-btn-secondary mm-lt-footer-cta mm-overtitre-xlarge" data-lens-tunnel-next ${disabled ? 'disabled' : ''} data-button-animate-chars>${this._escape(this._t('engraving.cta_finish', 'Terminer ma configuration'))}</button>`;
		} else if (stepsWithCta.includes(stepKey)) {
			actionBtn = `<button type="button" class="mm-btn mm-btn-secondary mm-lt-footer-cta mm-overtitre-xlarge" data-lens-tunnel-next data-button-animate-chars>${this._escape(this._t('nav.next', 'Étape suivante'))}</button>`;
		}

		this.dom.footer.innerHTML = this._buildFooterHtml(actionBtn);
		window.initButtonCharacterStagger?.();
	},

	/**
	 * Markup commun du footer : nom monture + total cumulé + CTA passé + mention livraison.
	 * Utilisé par `_updateFooter` (pendant le parcours) et `_validate` (après validation).
	 */
	_buildFooterHtml(actionHtml) {
		const title = this.state.productTitle || '';
		const summary = title ? `
			<div class="mm-lt-footer-summary">
				<span class="text-sm">${this._escape(title)}</span>
				<span class="text-sm" data-lens-tunnel-footer-total>${this._formatPrice(this._getGrandTotal())}</span>
			</div>
		` : '';
		const shipping = this.state.shippingText
			? `<p class="text-xs mm-black-500 mm-centered">${this._escape(this.state.shippingText)}</p>`
			: '';
		return `
			${summary}
			${actionHtml ? `<div class="mm-lt-footer-actions">${actionHtml}</div>` : ''}
			${shipping}
		`;
	},

	/**
	 * Total monture + options en euros (TVA incluse, prix client final).
	 */
	_getGrandTotal() {
		const frameEur = (this.state.productPrice || 0) / 100;
		return frameEur + this._getTotalExtra();
	},

	/**
	 * Nettoie un titre de variante Shopify pour afficher la couleur sans son suffixe SKU.
	 * Ex: "Or-RB3548N 001 / 54-21" → "Or / 54-21" (couleur cleanée, taille intacte).
	 * Règle : pour chaque segment séparé par " / ", si le segment commence par des lettres
	 * suivies d'un tiret, on ne garde que les lettres (la "couleur"). Sinon (segment
	 * purement numérique type "54-21"), on le garde tel quel.
	 */
	_cleanVariantTitle(title) {
		return String(title || '').split(' / ').map(seg => {
			const trimmed = seg.trim();
			const m = trimmed.match(/^([A-Za-zÀ-ÿ]+(?:\s+[A-Za-zÀ-ÿ]+)*)\s*-/);
			return m ? m[1].trim() : trimmed;
		}).join(' / ');
	},

	/**
	 * Format un montant en EUR au format FR : 320,00€
	 */
	_formatPrice(eurAmount) {
		const v = Number(eurAmount) || 0;
		return v.toFixed(2).replace('.', ',') + '€';
	},

	/* ====================================================================== */
	/* VALIDATE (dry-run)                                                      */
	/* ====================================================================== */

	async _validate() {
		const final = this._getFinalCombination();
		if (!final) return;

		const lensMap = await this._loadLensProductsMap();
		const verreEntry = this._findLensEntry(lensMap, final.optimum_ref);

		if (!verreEntry || !verreEntry.variant_id) {
			window.log('error', '🔭 [LENS_TUNNEL]', 'Verre introuvable dans lens-products-map.json', { optimum_ref: final.optimum_ref });
			this.dom.content.innerHTML += `
				<div class="mm-lt-error" role="alert" style="background:var(--error,#c0392b);color:var(--white);">
					Verre introuvable : <strong>${this._escape(final.optimum_ref || '')}</strong>.
					Verifiez que le produit existe avec le tag <code>verre-optimum</code> et le metafield <code>custom.verre_optimum</code>, puis regenerez le map.
				</div>
			`;
			return;
		}

		const lensOptions = [final.type_verre, final.taille_verre, final.categorie, final.sous_categorie, final.couleur, final.cat_solaire, final.anti_reflet]
			.filter(o => o && o.label)
			.map(o => ({ label: o.label }));

		const ord = this.state.ordonnance;
		/* Parcours sans-correction = pas de saisie d'ordonnance => on remonte un
		 * marqueur clair "Sans correction" au lieu d'un objet rempli de null
		 * (qui pretait a confusion : "les corrections ne remontent pas" alors qu'il
		 * n'y en a juste pas a remonter pour ce parcours). */
		let prescription = null;
		if (this.state.parcours === 'sans-correction') {
			prescription = 'Sans correction';
		} else if (ord && !ord.skipped) {
			prescription = {
				od: {
					sphere: ord.sphere_d || null,
					cylindre: ord.cylindre_d || null,
					axe: ord.axe_d || null,
					ecart: ord.ecart_d || null,
				},
				og: {
					sphere: ord.sphere_g || null,
					cylindre: ord.cylindre_g || null,
					axe: ord.axe_g || null,
					ecart: ord.ecart_g || null,
				},
				addition: ord.addition || null,
				distance_pupillaire: ord.distance_p || null,
			};
		}

		const montureVariantId = this.state.montureVariantId;
		/* Bundle ID unique pour cette paire monture+verres+extras.
		 * Sans cette property, Shopify consolide les 2 lignes monture (meme variant_id)
		 * en 1 ligne Qte=2, et on perd la separation des verres associes a chaque paire.
		 * En ajoutant _bundle_id en property sur la monture ET sur tous les nested items,
		 * Shopify garde les lignes distinctes (properties differentes = lignes differentes)
		 * et le rendu cart peut grouper visuellement par bundle. */
		const bundleId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

		// Cart AJAX API : `parent_id` cree un vrai nested cart line natif Shopify.
		// Visible groupé dans le cart drawer ET dans le checkout/email/order.
		const items = [
			{
				id: montureVariantId,
				quantity: 1,
				properties: {
					_bundle_id: bundleId,
					// Precommande : marque la ligne monture pour Klaviyo (relance panier, flow precommande)
					...(this.state.monturePrecommande && { _precommande: 'true' }),
					// Disponibilite et livraison estimee de la fiche : affichees dans l'espace client
					...(this.state.montureDisponibilite && { _disponibilite: this.state.montureDisponibilite }),
					...(this.state.montureLivraison && { _livraison_estimee: this.state.montureLivraison }),
				},
			},
			{
				id: verreEntry.variant_id,
				quantity: 1,
				parent_id: montureVariantId,
				properties: {
					_bundle_id: bundleId,
					_optimum_ref: final.optimum_ref,
					_lens_type: final.type_verre?.label || '',
					_lens_options: JSON.stringify(lensOptions),
					_lens_prescription: prescription
						? (typeof prescription === 'string' ? prescription : JSON.stringify(prescription))
						: '',
					_lens_prescription_url: ord?.file?.url || '',
				},
			},
		];

		// Gravure : nested item natif (parent_id = monture)
		const engravingItem = this._buildEngravingItem(montureVariantId);
		if (engravingItem) {
			engravingItem.properties = { ...(engravingItem.properties || {}), _bundle_id: bundleId };
			items.push(engravingItem);
		}

		// Art du détail (ex: Diamond cut) : nested item natif si le block a un produit lié
		const artDetailItem = this._buildArtDetailItem(montureVariantId);
		if (artDetailItem) {
			artDetailItem.properties = { ...(artDetailItem.properties || {}), _bundle_id: bundleId };
			items.push(artDetailItem);
		}

		window.log('info', '🔭 [LENS_TUNNEL]', 'Adding to cart', { items });

		// Spinner sur le bouton Valider du recap
		const validateBtn = this.dom.footer?.querySelector('[data-lens-tunnel-validate], [data-lens-tunnel-restart]');
		if (validateBtn) {
			validateBtn.disabled = true;
			validateBtn.classList.add('mm-loading');
			validateBtn.setAttribute('aria-busy', 'true');
		}

		try {
			// Inclut le section ID du cart drawer pour recevoir le HTML refresh
			const cartSectionId = (typeof window.mmGetCartSectionId === 'function') ? window.mmGetCartSectionId() : '';
			const body = { items };
			if (cartSectionId) body.sections = cartSectionId;

			const res = await fetch('/cart/add.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify(body),
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.description || err.message || `HTTP ${res.status}`);
			}
			const data = await res.json();
			window.log('info', '🔭 [LENS_TUNNEL]', 'Cart add success', { added: data.items?.length });

			// Refresh cart UI (count bubble + drawer HTML si data.sections present)
			if (typeof window.mmRefreshCartUI === 'function') {
				window.mmRefreshCartUI(data);
			}

			/* Publie le standard event Shopify Web Pixels pour le tunnel verres
			 * (Addingwell/GA4/Meta). data.items[] contient la monture + 2 verres + extras. */
			if (typeof window.mmPublishCartAdd === 'function') {
				window.mmPublishCartAdd(data);
			}

			window.emit('global:lens-tunnel-validated', { items, response: data });
			window.emit('global:cart-updated');

			/* Upload ordonnance vers Optimum (HDS) — déclenché au moment de l'ajout au panier
			 * (commit de la commande), pas en cours de tunnel. Non bloquant : si l'upload échoue
			 * le client a déjà sa monture+verre au panier, l'opticien sera notifié autrement. */
			if (this.state.ordonnance?.file && this._isValidEmail(this.state.ordonnance.email)) {
				this._uploadOrdoToOptimum().catch((err) => {
					window.log('warn', '🔭 [LENS_TUNNEL]', 'Optimum ordo upload failed (non-blocking)', { error: err.message });
				});
			}

			// Ferme le drawer du tunnel puis ouvre le cart drawer (deja rafraichi)
			this.close();
			setTimeout(() => {
				if (typeof window.mmOpenCart === 'function') window.mmOpenCart();
			}, 350);
		} catch (e) {
			window.log('error', '🔭 [LENS_TUNNEL]', 'Cart add failed', { error: e.message });
			if (validateBtn) {
				validateBtn.disabled = false;
				validateBtn.classList.remove('mm-loading');
				validateBtn.removeAttribute('aria-busy');
			}
			this.dom.content.innerHTML += `
				<div class="mm-lt-error" role="alert" style="background:var(--error,#c0392b);color:var(--white);">
					Ajout au panier impossible : ${this._escape(e.message)}
				</div>
			`;
		}
	},

	async _loadLensProductsMap() {
		if (this._lensMap) return this._lensMap;
		try {
			const url = this.dom?.wrapper?.dataset?.lensProductsMapUrl;
			if (!url) throw new Error('data-lens-products-map-url manquant sur le wrapper');
			const res = await fetch(url);
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			this._lensMap = await res.json();
			return this._lensMap;
		} catch (e) {
			window.log('error', '🔭 [LENS_TUNNEL]', 'Failed to load lens products map', { error: e.message });
			return null;
		}
	},

	/* Normalise un nom de verre Optimum : collapse les whitespaces (incl. doubles
	   espaces venant d'anciennes saisies) en un seul espace + trim. Tolere les
	   divergences de typo entre la ref CSV (clean) et le metafield Shopify
	   custom.verre_optimum (qui peut contenir des doubles espaces historiques sur
	   ~84 verres au 2026-06-29). */
	_normalizeOptimumRef(ref) {
		return String(ref || '').replace(/\s+/g, ' ').trim();
	},

	/* Lookup tolerant : tente d'abord un match exact, puis fallback sur un index
	   par ref normalisee construit a la volee (1x). Log un warn quand on tombe sur
	   le fallback — sert a identifier les metafields a nettoyer cote Shopify. */
	_findLensEntry(lensMap, ref) {
		if (!lensMap || !lensMap.map || !ref) return null;
		const exact = lensMap.map[ref];
		if (exact) return exact;

		if (!lensMap._normIndex) {
			lensMap._normIndex = Object.create(null);
			for (const k of Object.keys(lensMap.map)) {
				lensMap._normIndex[this._normalizeOptimumRef(k)] = lensMap.map[k];
			}
		}
		const norm = this._normalizeOptimumRef(ref);
		const fuzzy = lensMap._normIndex[norm];
		if (fuzzy) {
			window.log('warn', '🔭 [LENS_TUNNEL]', 'Verre matche via fallback normalise (whitespace diff Shopify vs CSV)', { ref });
			return fuzzy;
		}
		return null;
	},

	/**
	 * Construit le nested cart line pour le produit Gravure (variantes 1/2/3/4 gravures).
	 * La couleur (transparent/gold/silver) est portee par la property _engraving_color.
	 *
	 * @param {number} parentVariantId - Variant ID de la monture (devient parent_id natif Shopify)
	 */
	_buildEngravingItem(parentVariantId) {
		if (!this._hasAnyEngravingZone()) return null;
		const color = this.state.engraving?.color || 'transparent';
		/* Sélection du produit selon la couleur, fallback transparent */
		const products = window.MM_LT_ENGRAVING_PRODUCTS || {};
		const product = products[color] || products.transparent || window.MM_LT_ENGRAVING_PRODUCT;
		if (!product) {
			window.log('warn', '🔭 [LENS_TUNNEL]', `Aucun produit Gravure configuré (couleur: ${color})`);
			return null;
		}

		/* Table de correspondance code CSS -> nom lisible de la police.
		 * Le code interne (aa1..aa4) est utilise pour piloter les classes CSS
		 * d'apercu, mais dans le cart et le BO admin on veut le nom humain
		 * pour que l'opticien sache quelle police graver. */
		const FONT_LABELS = { aa1: 'Space Grotesk', aa2: 'Georgia', aa3: 'Brush Script', aa4: 'Impact' };
		const zoneKeys = ['lt', 'lb', 'rt', 'rb'];
		const engravedZones = zoneKeys
			.filter(k => this._isZoneEngraved(this.state.engraving.zones[k]))
			.map(k => {
				const zoneData = this.state.engraving.zones[k];
				const fontCode = zoneData.font || null;
				return {
					zone: k,
					label: this._t(`engraving.zone_${k}`, k),
					text: zoneData.text,
					font: fontCode,
					font_name: fontCode ? (FONT_LABELS[fontCode] || fontCode) : null,
				};
			});
		const count = engravedZones.length;
		if (count === 0) return null;

		const matchedVariant = (product.variants || []).find(v => {
			const m = String(v.title || '').match(/(\d+)/);
			return m && Number(m[1]) === count;
		});
		if (!matchedVariant) {
			window.log('warn', '🔭 [LENS_TUNNEL]', `Aucune variante "${count} gravure(s)" trouvee`, { variants: product.variants });
			return null;
		}

		/* Format lisible pour le BO admin Shopify (opticien) : une property par zone
		 * gravee, avec cle FR ex "Gravure Œil Gauche Haut" et valeur `"text" — Police`.
		 * Sans '_' = visibles dans admin/checkout/email. Les _engraving_* techniques
		 * restent pour le cart popup client (mm_cart_detail.js lit le JSON). */
		const colorLabel = this._t(`engraving.color_${color}`, color);
		const properties = {
			_engraving_color: color,
			_engraving_zone_count: String(count),
			_engraving_zones: JSON.stringify(engravedZones),
			[this._t('engraving.property_color', 'Couleur gravure')]: colorLabel,
		};
		const propPrefix = this._t('engraving.property_zone_prefix', 'Gravure');
		for (const z of engravedZones) {
			const zoneKey = `${propPrefix} ${z.label}`;
			const zoneVal = z.font_name ? `"${z.text}" — ${z.font_name}` : `"${z.text}"`;
			properties[zoneKey] = zoneVal;
		}

		return {
			id: matchedVariant.id,
			quantity: 1,
			parent_id: parentVariantId,
			properties,
		};
	},

	/**
	 * Construit le nested cart line pour le produit Art du détail sélectionné (ex: Diamond cut).
	 * Le block art_detail_option a un setting `product` (1ère variante utilisée).
	 * Si aucun art_detail sélectionné OU s'il n'a pas de produit lié → retourne null.
	 *
	 * @param {number} parentVariantId - Variant ID de la monture
	 */
	_buildArtDetailItem(parentVariantId) {
		const artId = this.state.artDetail;
		if (!artId) return null;
		const artOpt = (this.state.artDetailOptions || []).find(o => o.id === artId);
		if (!artOpt || !artOpt.variant_id) return null;
		return {
			id: artOpt.variant_id,
			quantity: 1,
			parent_id: parentVariantId,
			properties: {
				_art_detail_label: this._getEngravingDisplayLabel(artOpt) || artOpt.label || '',
			},
		};
	},

	_restart() {
		this._resetState();
		this.state.steps = ['correction'];
		this.state.stepIndex = 0;
		this._render();
	},

	/* ====================================================================== */
	/* QUOTE PDF (génération côté client via jsPDF lazy-loadé depuis CDN)      */
	/* ====================================================================== */

	/* Charge le logo configure (image_picker) en base64 + ratio, stocke dans devisSettings.
	 * Appele a l'init du tunnel pour avoir le logo pret quand l'utilisateur genere le devis.
	 * Silencieux si pas de logo configure ou si le fetch echoue (le devis se rendra sans logo). */
	_preloadDevisLogo() {
		const url = this.state.devisSettings?.logoUrl;
		if (!url) return;
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			try {
				const canvas = document.createElement('canvas');
				canvas.width = img.naturalWidth;
				canvas.height = img.naturalHeight;
				canvas.getContext('2d').drawImage(img, 0, 0);
				this.state.devisSettings.logoBase64 = canvas.toDataURL('image/png');
				this.state.devisSettings.logoAspectRatio = img.naturalWidth / Math.max(1, img.naturalHeight);
			} catch (err) {
				window.log('warn', '🔭 [LENS_TUNNEL]', 'Logo preload canvas failed', { error: err.message });
			}
		};
		img.onerror = () => window.log('warn', '🔭 [LENS_TUNNEL]', 'Logo preload fetch failed', { url });
		img.src = url;
	},

	/* Lazy-load de la table de lookup LPP (theme/assets/lens-lpp-table.json).
	 * Promise partagee pour gerer les calls concurrents. Retourne true si la table
	 * est dispo apres l'appel, false sinon (le devis tournera avec fallbacks). */
	_loadLensLppTable() {
		if (this.state.lensLppTable) return Promise.resolve(true);
		if (this._lensLppPromise) return this._lensLppPromise;
		const url = this.state.lensLppUrl;
		if (!url) return Promise.resolve(false);
		this._lensLppPromise = fetch(url)
			.then((res) => res.ok ? res.json() : null)
			.then((json) => {
				if (json && Array.isArray(json.table)) {
					this.state.lensLppTable = json.table;
					return true;
				}
				return false;
			})
			.catch((err) => {
				window.log('warn', '🔭 [LENS_TUNNEL]', 'lens-lpp-table fetch failed', { error: err.message });
				return false;
			});
		return this._lensLppPromise;
	},

	/* Lookup d'une ligne LPP exacte pour (zone, type, sphere, cyl).
	 * Retourne { regroup, lpp, rbst, px } ou null si rien ne match.
	 * - sphereStr/cylindreStr : chaines FR ("-2,50") ou vides
	 * - zone : 'A' (100% sante) ou 'B' (offre client premium)
	 *
	 * Logique de match : iterate la table filtree par zone+category, et applique
	 * les criteres de match.match (sphere range + signs + cyl range + S formula).
	 * Cf. scripts/build-lens-lpp-table.mjs pour la structure des entries. */
	_lookupLensLpp(zone, isProgressif, sphereStr, cylindreStr) {
		if (!this.state.lensLppTable) return null;
		if (sphereStr == null || sphereStr === '') return null;
		const sphere = parseFloat(String(sphereStr).replace(',', '.'));
		if (isNaN(sphere)) return null;
		const cylindre = Math.abs(parseFloat(String(cylindreStr || '0').replace(',', '.'))) || 0;

		const category = isProgressif ? 'progressif' : 'unifocal';
		const absSphere = Math.abs(sphere);
		const sign = sphere > 0 ? 'pos' : sphere < 0 ? 'neg' : 'neutre';

		for (const row of this.state.lensLppTable) {
			if (row.zone !== zone || row.category !== category) continue;
			const m = row.match;
			if (m.neutre) {
				if (sphere === 0 && cylindre === 0) return row;
				continue;
			}
			/* Match cyl presence/range */
			if (m.cylRange === null) {
				if (cylindre !== 0) continue;
			} else {
				if (cylindre < m.cylRange[0] || cylindre > m.cylRange[1]) continue;
			}
			/* S formula : positive sphere + cyl, compare S = sphere + cyl/2 */
			if (m.useSFormula) {
				if (sphere <= 0) continue;
				const S = sphere + cylindre / 2;
				if (S > m.sphereRange[0] && S <= m.sphereRange[1]) return row;
				continue;
			}
			/* Sphere sign + abs range */
			if (!m.spheres.includes(sign)) continue;
			if (absSphere > m.sphereRange[0] && absSphere <= m.sphereRange[1]) return row;
		}
		return null;
	},

	/* Base de remboursement SS pour la monture (regle reglementaire) :
	 *  - Prix monture <= 30 EUR  -> 9 EUR rembourses
	 *  - Prix monture > 30 EUR   -> 0,05 EUR (symbolique)
	 * Cf. brief Celine 2026-08. */
	_getFrameRbst(priceEur) {
		return priceEur <= 30 ? 9 : 0.05;
	},

	/* Format FR du prix EUR : "9,75 €" / "0,05 €" / "32,50 €".
	 * Utilise par les rows du devis (colonnes Px et Rbst SS). */
	_formatEur(n) {
		if (n == null || isNaN(n)) return '—';
		return `${Number(n).toFixed(2).replace('.', ',')} €`;
	},

	_loadJsPdf() {
		if (window.jspdf?.jsPDF) return Promise.resolve();
		if (this._jsPdfPromise) return this._jsPdfPromise;
		this._jsPdfPromise = new Promise((resolve, reject) => {
			const s = document.createElement('script');
			s.src = this.config.jsPdfCdn;
			s.async = true;
			s.onload = () => resolve();
			s.onerror = () => {
				this._jsPdfPromise = null;
				reject(new Error('jsPDF failed to load'));
			};
			document.head.appendChild(s);
		});
		return this._jsPdfPromise;
	},

	async _downloadQuote(btn) {
		const final = this._getFinalCombination();
		if (!final) return;

		/* On affiche TOUJOURS la modal d'identité, même si l'identité du customer Shopify
		 * loggué est déjà connue. Raison : le devis est un document legal qui doit afficher
		 * le nom du patient (potentiellement différent du compte Shopify : devis pour un
		 * conjoint, un enfant, etc.). La modal pré-remplit avec les infos du compte mais
		 * permet l'édition. L'utilisateur peut annuler -> pas de download.
		 * La modal collecte aussi la correction (sphère/cyl/axe par oeil + addition) si
		 * l'ordo a ete uploadee sans saisie — necessaire pour calculer le code regroupement. */
		const identity = await this._promptDevisIdentity();
		if (!identity) return;
		const { correction, ...identityFields } = identity;
		this.state.identity = { ...this.state.identity, ...identityFields };
		if (correction) {
			this.state.ordonnance = { ...this.state.ordonnance, ...correction };
		}

		const originalHtml = btn.innerHTML;
		btn.disabled = true;
		btn.textContent = this._t('quote.download_loading', 'Génération…');
		try {
			/* Charge jsPDF + table LPP en parallele pour minimiser le delai
			 * percu par l'utilisateur (jsPDF ~150 KB, lens-lpp-table.json ~12 KB). */
			await Promise.all([
				this._loadJsPdf(),
				this._loadLensLppTable(),
			]);
			const { doc, filename, ref, total } = this._generateQuotePdf(final);

			/* Upload Optimum non bloquant : si ça échoue, le client a quand même son PDF.
			 * Sans GCF configurée → dry-run (log console uniquement, défini dans _uploadDevisToOptimum). */
			if (this._hasFullIdentity()) {
				const blob = doc.output('blob');
				this._uploadDevisToOptimum(blob, filename).catch((err) => {
					window.log('warn', '🔭 [LENS_TUNNEL]', 'Optimum devis upload failed (non-blocking)', { error: err.message });
				});
			}

			doc.save(filename);
			window.emit('global:lens-tunnel-quote-downloaded', { ref, total });
		} catch (err) {
			window.log('error', '🔭 [LENS_TUNNEL]', 'Quote generation failed', { error: err.message });
			alert(this._t('quote.download_error', 'Erreur génération devis'));
		} finally {
			btn.disabled = false;
			btn.innerHTML = originalHtml;
		}
	},

	_hasFullIdentity() {
		const id = this.state.identity;
		return this._isValidEmail(id.email) && !!(id.firstName || '').trim() && !!(id.lastName || '').trim();
	},

	/* Modal collecte identité (email + prénom + nom) — résout avec { email, firstName, lastName }
	 * ou null si l'utilisateur annule. */
	_promptDevisIdentity() {
		return new Promise((resolve) => {
			const wrapper = this.dom.wrapper;
			wrapper.querySelector('.mm-lt-identity-modal')?.remove();

			const id = this.state.identity;
			const ord = this.state.ordonnance || {};
			/* Cas ordo uploadee sans saisie correction : on ajoute les champs sphere/cyl/axe
			 * par oeil + addition (si parcours le requiert) directement dans cette modal pour
			 * recuperer la prescription necessaire au calcul du code de regroupement.
			 * Si l'user a deja saisi (au moins un sphere), on n'embete pas avec ces champs. */
			const isCorrective = this.state.parcours && this.state.parcours !== 'sans-correction';
			const needsCorrection = isCorrective && !ord.sphere_d && !ord.sphere_g;
			const needsAddition = this.state.parcours === 'progressifs' || this.state.parcours === 'vision-pres';

			const eyeFieldsHtml = (eyeKey, eyeLabel) => `
				<div class="mm-lt-identity-eye" data-eye="${eyeKey}">
					<p class="mm-lt-identity-eye-label text-sm mm-black-500">${this._escape(eyeLabel)}</p>
					<div class="mm-lt-identity-eye-row">
						<label class="mm-lt-identity-field">
							<span class="text-xs">${this._escape(this._t('identity.sphere_label', 'Sphère'))}</span>
							<input type="text" name="sphere_${eyeKey}" value="${this._escape(ord[`sphere_${eyeKey}`] || '')}" placeholder="-2,50" inputmode="decimal" required>
						</label>
						<label class="mm-lt-identity-field">
							<span class="text-xs">${this._escape(this._t('identity.cylindre_label', 'Cylindre'))}</span>
							<input type="text" name="cylindre_${eyeKey}" value="${this._escape(ord[`cylindre_${eyeKey}`] || '')}" placeholder="-0,75" inputmode="decimal">
						</label>
						<label class="mm-lt-identity-field">
							<span class="text-xs">${this._escape(this._t('identity.axe_label', 'Axe (°)'))}</span>
							<input type="number" name="axe_${eyeKey}" value="${this._escape(ord[`axe_${eyeKey}`] || '')}" placeholder="90" min="0" max="180" step="1">
						</label>
					</div>
				</div>
			`;

			const correctionHtml = needsCorrection ? `
				<fieldset class="mm-lt-identity-fieldset">
					<legend class="mm-lt-identity-legend text-sm">${this._escape(this._t('identity.correction_legend', 'Votre correction'))}</legend>
					<p class="mm-lt-identity-info text-xs mm-black-500">${this._escape(this._t('identity.correction_hint', "Renseignez les valeurs de votre ordonnance pour générer un devis conforme."))}</p>
					${eyeFieldsHtml('d', this._t('ordonnance.eye_right', 'Œil droit'))}
					${eyeFieldsHtml('g', this._t('ordonnance.eye_left', 'Œil gauche'))}
					${needsAddition ? `
						<label class="mm-lt-identity-field">
							<span class="text-xs">${this._escape(this._t('identity.addition_label', 'Addition (identique OD/OG)'))}</span>
							<input type="text" name="addition" value="${this._escape(ord.addition || '')}" placeholder="+2,00" inputmode="decimal" required>
						</label>
					` : ''}
				</fieldset>
			` : '';

			const modal = document.createElement('div');
			modal.className = 'mm-lt-identity-modal';
			modal.setAttribute('role', 'dialog');
			modal.setAttribute('aria-modal', 'true');
			modal.setAttribute('aria-labelledby', 'mm-lt-identity-title');
			modal.innerHTML = `
				<div class="mm-lt-identity-modal-overlay" data-lens-identity-cancel></div>
				<div class="mm-lt-identity-modal-content">
					<h3 id="mm-lt-identity-title" class="mm-lt-identity-title text-md">${this._escape(this._t('identity.title', 'Vos coordonnées'))}</h3>
					<p class="mm-lt-identity-info text-sm mm-black-500">${this._escape(this._t('identity.hds_info', "Pour enregistrer votre devis, nous avons besoin de ces informations. Notre hébergement est certifié HDS, garantissant la sécurité de vos données."))}</p>
					<form class="mm-lt-identity-form" data-lens-identity-form novalidate>
						<label class="mm-lt-identity-field">
							<span class="text-sm">${this._escape(this._t('identity.firstName_label', 'Prénom'))}</span>
							<input type="text" name="firstName" value="${this._escape(id.firstName || '')}" required autocomplete="given-name">
						</label>
						<label class="mm-lt-identity-field">
							<span class="text-sm">${this._escape(this._t('identity.lastName_label', 'Nom'))}</span>
							<input type="text" name="lastName" value="${this._escape(id.lastName || '')}" required autocomplete="family-name">
						</label>
						<label class="mm-lt-identity-field">
							<span class="text-sm">${this._escape(this._t('identity.email_label', 'Email'))}</span>
							<input type="email" name="email" value="${this._escape(id.email || '')}" required autocomplete="email">
						</label>
						${correctionHtml}
						<p class="mm-lt-identity-error text-xs" data-lens-identity-error aria-live="assertive" hidden></p>
						<div class="mm-lt-identity-actions">
							<button type="button" class="mm-btn mm-btn-tertiary" data-lens-identity-cancel>${this._escape(this._t('nav.cancel', 'Annuler'))}</button>
							<button type="submit" class="mm-btn mm-btn-primary">${this._escape(this._t('identity.submit', 'Continuer'))}</button>
						</div>
					</form>
				</div>
			`;
			wrapper.appendChild(modal);

			const cleanup = () => modal.remove();

			modal.querySelectorAll('[data-lens-identity-cancel]').forEach((el) => {
				el.addEventListener('click', () => {
					cleanup();
					resolve(null);
				});
			});

			const form = modal.querySelector('[data-lens-identity-form]');
			const errEl = modal.querySelector('[data-lens-identity-error]');
			form.addEventListener('submit', (e) => {
				e.preventDefault();
				const fd = new FormData(form);
				const firstName = (fd.get('firstName') || '').toString().trim();
				const lastName = (fd.get('lastName') || '').toString().trim();
				const email = (fd.get('email') || '').toString().trim();
				if (!firstName || !lastName || !this._isValidEmail(email)) {
					errEl.textContent = this._t('errors.identity_required', 'Tous les champs sont requis et l\'email doit être valide.');
					errEl.hidden = false;
					return;
				}

				const result = { firstName, lastName, email };

				/* Validation correction (uniquement si on a affiche les champs) */
				if (needsCorrection) {
					const parseOpti = (raw) => {
						if (!raw) return null;
						const n = parseFloat(String(raw).replace(',', '.').replace(/\s/g, ''));
						return isNaN(n) ? null : n;
					};
					const fmtOpti = (n, signed = true) => {
						const sign = (signed && n > 0) ? '+' : '';
						return `${sign}${n.toFixed(2).replace('.', ',')}`;
					};

					const correction = {};
					let errMsg = '';
					for (const eyeKey of ['d', 'g']) {
						const sphRaw = (fd.get(`sphere_${eyeKey}`) || '').toString().trim();
						const cylRaw = (fd.get(`cylindre_${eyeKey}`) || '').toString().trim();
						const axeRaw = (fd.get(`axe_${eyeKey}`) || '').toString().trim();

						const sph = parseOpti(sphRaw);
						if (sph === null || sph < -20 || sph > 20) {
							errMsg = this._t('errors.sphere_invalid', 'Sphère invalide (entre -20,00 et +20,00).');
							break;
						}
						correction[`sphere_${eyeKey}`] = fmtOpti(sph, true);

						if (cylRaw) {
							const cyl = parseOpti(cylRaw);
							if (cyl === null || cyl > 0 || cyl < -10) {
								errMsg = this._t('errors.cylindre_invalid', 'Cylindre invalide (négatif, entre -10,00 et 0).');
								break;
							}
							correction[`cylindre_${eyeKey}`] = fmtOpti(cyl, true);

							/* Axe obligatoire si cylindre saisi */
							const axe = parseInt(axeRaw, 10);
							if (isNaN(axe) || axe < 0 || axe > 180) {
								errMsg = this._t('errors.axe_required', 'Axe requis (entre 0 et 180°) si cylindre.');
								break;
							}
							correction[`axe_${eyeKey}`] = String(axe);
						}
					}

					if (!errMsg && needsAddition) {
						const addRaw = (fd.get('addition') || '').toString().trim();
						const add = parseOpti(addRaw);
						if (add === null || add <= 0 || add > 4) {
							errMsg = this._t('errors.addition_invalid', 'Addition invalide (entre +0,25 et +4,00).');
						} else {
							correction.addition = fmtOpti(add, true);
						}
					}

					if (errMsg) {
						errEl.textContent = errMsg;
						errEl.hidden = false;
						return;
					}
					result.correction = correction;
				}

				cleanup();
				resolve(result);
			});

			setTimeout(() => modal.querySelector('input[name="firstName"]')?.focus(), 50);
		});
	},

	/* Upload du devis PDF vers Optimum via la GCF middleware. Reuse clientId/visiteId
	 * si déjà obtenus lors de l'upload de l'ordonnance.
	 * Sans GCF configurée → dry-run (log console uniquement, pas de fetch). */
	async _uploadDevisToOptimum(blob, filename) {
		const opt = this.state.optimum;
		const id = this.state.identity;
		if (!this._isValidEmail(id.email)) return;

		const base64 = await new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = String(reader.result || '');
				const idx = result.indexOf(',');
				resolve(idx >= 0 ? result.slice(idx + 1) : result);
			};
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(blob);
		});

		const body = {
			action: 'upload_devis',
			email: id.email.trim(),
			firstName: id.firstName || '',
			lastName: id.lastName || '',
			socialSecurityNumber: (this.state.ordonnance?.social_security_number || '').trim(),
			fileBase64: base64,
			fileType: 'pdf',
			fileName: filename,
			optimum_client_id: opt.clientId || null,
			visite_id: opt.visiteId || null,
		};

		if (!opt.gcfUrl) {
			this._logOptimumDryRun('upload_devis', body);
			opt.clientId = opt.clientId || `DRYRUN_CLIENT_${Date.now().toString(36).toUpperCase()}`;
			opt.visiteId = opt.visiteId || `DRYRUN_VISITE_${Date.now().toString(36).toUpperCase()}`;
			opt.devisUploaded = true;
			return;
		}

		const res = await fetch(opt.gcfUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Theme-Secret': opt.themeSecret,
			},
			body: JSON.stringify(body),
		});
		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			throw new Error(data.error || `HTTP ${res.status}`);
		}
		const data = await res.json();
		opt.clientId = data.optimum_client_id || opt.clientId;
		opt.visiteId = data.visite_id || opt.visiteId;
		opt.devisUploaded = true;
		window.log('info', '🔭 [LENS_TUNNEL]', 'Optimum devis uploaded', { clientId: opt.clientId, visiteId: opt.visiteId });
	},

	_generateQuotePdf(final) {
		const { jsPDF } = window.jspdf;
		const doc = new jsPDF({ unit: 'mm', format: 'a4' });
		const pageWidth = doc.internal.pageSize.getWidth();
		const marginX = 20;
		const contentWidth = pageWidth - 2 * marginX;
		let y = 20;

		const ref = 'DEV-' + Date.now().toString(36).toUpperCase();
		const todayStr = new Date().toLocaleDateString('fr-FR');
		const totalExtra = this._getTotalExtra();
		const ord = this.state.ordonnance || {};
		const finess = this.state.devisSettings?.finessNumber || '';

		/* Helpers */
		const section = (title) => {
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(11);
			doc.setTextColor(0);
			doc.text(title, marginX, y);
			y += 5;
			doc.setDrawColor(200);
			doc.line(marginX, y, pageWidth - marginX, y);
			y += 5;
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(10);
		};
		const line = (label, value) => {
			doc.text(label, marginX, y);
			doc.text(String(value), pageWidth - marginX, y, { align: 'right' });
			y += 5.5;
		};
		/* Ligne tableau 3 colonnes : label | code | prix */
		const rowLpp = (label, code, price) => {
			doc.text(label, marginX, y);
			doc.setTextColor(110);
			doc.text(code || '—', marginX + 110, y);
			doc.setTextColor(0);
			doc.text(price, pageWidth - marginX, y, { align: 'right' });
			y += 5.5;
		};
		const wrappedLine = (text) => {
			const wrapped = doc.splitTextToSize(text, contentWidth);
			doc.text(wrapped, marginX, y);
			y += 5.5 * wrapped.length;
		};
		const ensureSpace = (needed) => {
			if (y + needed > 280) {
				doc.addPage();
				y = 20;
			}
		};
		const fmtDate = (iso) => {
			if (!iso) return '';
			const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
			return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
		};

		/* ============== HEADER 2 COLONNES ==============
		 * GAUCHE  : Logo + Infos societe (richtext) + N° FINESS
		 * DROITE  : Date / N° devis / Valable jusqu'au / Patient (nom + email + N° SS + ordo) */
		const ds = this.state.devisSettings || {};
		const idPatient = this.state.identity || {};
		const patientName = [idPatient.firstName, idPatient.lastName].filter(Boolean).join(' ').trim();

		/* Date validite = today + 2 mois (cf. brief : devis valable 2 mois) */
		const validityDate = new Date();
		validityDate.setMonth(validityDate.getMonth() + 2);
		const validityStr = validityDate.toLocaleDateString('fr-FR');

		let leftY = y;
		let rightY = y;

		/* COLONNE GAUCHE */
		if (ds.logoBase64) {
			try {
				const logoW = 40;
				const logoH = logoW / (ds.logoAspectRatio || 2);
				doc.addImage(ds.logoBase64, 'PNG', marginX, leftY, logoW, logoH);
				leftY += logoH + 3;
			} catch (err) {
				window.log('warn', '🔭 [LENS_TUNNEL]', 'Logo addImage failed', { error: err.message });
			}
		}
		const companyPlain = this._richtextToPlainText(ds.companyInfo || '');
		if (companyPlain) {
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(9);
			doc.setTextColor(60);
			const companyLines = doc.splitTextToSize(companyPlain, 85);
			doc.text(companyLines, marginX, leftY);
			leftY += 4.2 * companyLines.length + 2;
			doc.setTextColor(0);
		}
		if (finess) {
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(9);
			doc.text(`${this._t('quote.finess_label', 'N° FINESS')} : ${finess}`, marginX, leftY);
			leftY += 5;
		} else {
			doc.setFontSize(8);
			doc.setTextColor(200, 0, 0);
			doc.text('[N° FINESS à renseigner dans Customizer]', marginX, leftY);
			doc.setTextColor(0);
			doc.setFontSize(9);
			leftY += 5;
		}

		/* COLONNE DROITE */
		const rightX = pageWidth - marginX;
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(9);
		doc.setTextColor(0);
		doc.text(todayStr, rightX, rightY, { align: 'right' });
		rightY += 4.5;
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(10);
		doc.text(`${this._t('quote.reference_label', 'Devis n°')} ${ref}`, rightX, rightY, { align: 'right' });
		rightY += 4.5;
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(9);
		doc.text(`${this._t('quote.validity_until', 'Valable jusqu\'au')} ${validityStr}`, rightX, rightY, { align: 'right' });
		rightY += 7;

		/* Bloc patient */
		if (patientName) {
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(10);
			doc.text(patientName, rightX, rightY, { align: 'right' });
			doc.setFont('helvetica', 'normal');
			rightY += 4.5;
		}
		doc.setFontSize(9);
		doc.setTextColor(60);
		if (idPatient.email) {
			doc.text(idPatient.email, rightX, rightY, { align: 'right' });
			rightY += 4;
		}
		if (ord.social_security_number) {
			doc.text(`${this._t('ordonnance.social_security', 'N° Sécurité Sociale')} : ${ord.social_security_number}`, rightX, rightY, { align: 'right' });
			rightY += 4;
		}
		if (ord.prescription_date) {
			doc.text(`${this._t('quote.prescription_date_label', 'Ordonnance du')} ${fmtDate(ord.prescription_date)}`, rightX, rightY, { align: 'right' });
			rightY += 4;
		}
		doc.setTextColor(0);

		/* Aligne y sur le bas le plus haut des 2 colonnes */
		y = Math.max(leftY, rightY) + 6;

		/* ============== TITRE "DEVIS NORMALISE" ============== */
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(16);
		doc.text(this._t('quote.title_normalized', 'DEVIS NORMALISÉ'), pageWidth / 2, y, { align: 'center' });
		y += 5;
		doc.setFont('helvetica', 'italic');
		doc.setFontSize(8);
		doc.setTextColor(120);
		doc.text(this._t('quote.client_copy', 'Exemplaire client'), pageWidth / 2, y, { align: 'center' });
		doc.setTextColor(0);
		doc.setFont('helvetica', 'normal');
		y += 8;

		/* ============== DONNEES PARTAGEES Zone A + Zone B ============== */

		/* Monture : meme produit pour les 2 zones, code regroupement different selon zone.
		 * V1 : tout traite comme adulte (cf. brief client, pas de detection enfant). */
		const framePriceEur = (this.state.productPrice || 0) / 100;
		const frameLabel = this.state.productTitle
			? (this.state.montureVariantTitle
				? `${this.state.productTitle} (${this.state.montureVariantTitle})`
				: this.state.productTitle)
			: '';
		const frameRegrpB = this._getFrameRegroupement(framePriceEur);
		/* Monture Zone A : si le marchand a configure un produit Shopify dans les settings,
		 * on l'utilise (titre + variant + prix reel). Sinon fallback sur la grille 100% sante
		 * generique (label generique + 30 EUR adulte). (ds deja declare dans le header) */
		const FRAME_A_PRICE_EUR = ds.zoneAFramePriceCents
			? ds.zoneAFramePriceCents / 100
			: 30;
		const frameLabelA = ds.zoneAFrameTitle
			? (ds.zoneAFrameVariantTitle && ds.zoneAFrameVariantTitle !== 'Default Title'
				? `${ds.zoneAFrameTitle} (${ds.zoneAFrameVariantTitle})`
				: ds.zoneAFrameTitle)
			: this._t('quote.zone_a_frame_default', 'Monture Classe A (offre 100% Santé)');
		const LENS_A_SINGLE_EUR = 32.5;     // 65 EUR par paire / 2 = 32,50 par verre
		const frameRegrpA = this._getFrameRegroupement(FRAME_A_PRICE_EUR);

		/* Verres : prix total client (somme delta options) divise par 2 pour chaque oeil */
		const lensPriceFields = ['type_verre', 'taille_verre', 'gamme_prog', 'categorie', 'sous_categorie', 'couleur', 'cat_solaire', 'anti_reflet'];
		let totalVerre = 0;
		const lensLabelParts = [];
		for (const key of lensPriceFields) {
			const opt = final[key];
			if (!opt?.label) continue;
			if (opt.price) totalVerre += Number(opt.price) || 0;
			lensLabelParts.push(this._label(opt));
		}
		const halfVerre = totalVerre / 2;
		const lensLabelB = lensLabelParts.join(' · ') || this._t('quote.lens_default', 'Verre');
		const isProgressif = this.state.parcours === 'progressifs';
		const isSolaire = this._isSolaire();

		/* Label Zone A des verres : nomenclature 100% sante reglementaire (Esprix) */
		const lensLabelA = isProgressif
			? this._t('quote.lens_zone_a_progressif', 'Esprix prog 1,5 AR')
			: this._t('quote.lens_zone_a_unifocal', 'Esprix UF 1,5 AR');

		/* Codes regroupement par oeil ({ zoneA, zoneB }) */
		const eyes = ['d', 'g'].map((eyeKey) => {
			const sphere = ord[`sphere_${eyeKey}`];
			const cylindre = ord[`cylindre_${eyeKey}`];
			const axe = ord[`axe_${eyeKey}`];
			const codes = this._getLensRegroupement(isProgressif, sphere, cylindre);
			return { eyeKey, sphere, cylindre, axe, codes };
		});

		/* Solaire : si le client a choisi une teinte (cat_solaire), on ajoute une ligne
		 * "OPTIQUE, SUPPLEMENT POUR VERRES TEINTES" code SV4 sans prix ni rbst dans les 2 zones. */
		const sv4Label = this._t('quote.sv4_label', 'OPTIQUE, SUPPLÉMENT POUR VERRES TEINTÉS');

		/* ============== HELPER : ligne tableau 4 colonnes ============== */
		/* Description | Regroup. | Rbst SS | Total TTC */
		const COL_REGROUP_X = marginX + 95;
		const COL_RBST_X    = marginX + 122;
		const COL_PRICE_X   = pageWidth - marginX;
		/* Largeur effective de la colonne Description : on garde 2mm de respiration
		 * avant la colonne Regroup pour eviter de coller le texte (ou pire, deborder). */
		const COL_DESC_WIDTH = (COL_REGROUP_X - marginX) - 2;

		const row4 = (label, code, rbst, price) => {
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(9);
			doc.setTextColor(0);
			/* Wrap automatique sur la colonne Description : noms de produits longs
			 * (montures Cartier, etc.) debordaient sur les colonnes Regroup/Rbst.
			 * splitTextToSize coupe sur les espaces et retourne un array de lignes. */
			const labelLines = doc.splitTextToSize(String(label || ''), COL_DESC_WIDTH);
			doc.text(labelLines, marginX, y);
			doc.setTextColor(110);
			/* Code + Rbst + Prix s'alignent sur la PREMIERE ligne du label (y de depart),
			 * pas sur la derniere — la convention LPP est que le code regroupe est associe
			 * a la ligne produit, pas a son detail. */
			doc.text(code || '—', COL_REGROUP_X, y);
			doc.text(rbst || '—', COL_RBST_X, y);
			doc.setTextColor(0);
			doc.text(price || '', COL_PRICE_X, y, { align: 'right' });
			y += 5.5 * labelLines.length;
		};

		const tableHeader = () => {
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(9);
			doc.setTextColor(60);
			doc.text(this._t('quote.col_description', 'Description'), marginX, y);
			doc.text(this._t('quote.col_regroup', 'Regroup.'), COL_REGROUP_X, y);
			/* Rbst SS peut etre sur 2 lignes (locale FR : "Base de\nremboursement"
			 * pour eviter le chevauchement avec la colonne Total TTC). jsPDF accepte
			 * un array pour rendre chaque ligne avec son line-spacing. */
			const rbstLabel = this._t('quote.col_rbst_ss', 'Rbst SS');
			const rbstParts = rbstLabel.split('\n');
			doc.text(rbstParts, COL_RBST_X, y);
			doc.text(this._t('quote.col_total_ttc', 'Total TTC'), COL_PRICE_X, y, { align: 'right' });
			/* Avance y selon le header le plus haut (le Rbst si multi-ligne) */
			y += rbstParts.length > 1 ? 3 + (rbstParts.length - 1) * 4 : 3;
			doc.setDrawColor(180);
			doc.line(marginX, y, pageWidth - marginX, y);
			y += 4;
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(9);
			doc.setTextColor(0);
		};

		const subLineCorrection = (eye) => {
			const subParts = [
				eye.sphere ? `${this._t('recap.sphere_short', 'Sph.')} ${eye.sphere}` : null,
				eye.cylindre ? `${this._t('recap.cylindre_short', 'Cyl.')} ${eye.cylindre}` : null,
				eye.axe ? `${this._t('recap.axe_short', 'Axe')} ${eye.axe}°` : null,
			].filter(Boolean);
			if (!subParts.length) return;
			doc.setFontSize(8);
			doc.setTextColor(110);
			doc.text(`  ${subParts.join(' / ')}`, marginX, y);
			doc.setFontSize(9);
			doc.setTextColor(0);
			y += 4.5;
		};

		/* ============== SECTION ZONE A "Offre 100% Santé" — FR UNIQUEMENT ==============
		 * L'offre 100% sante est une mesure reglementaire francaise (decret 2019-21).
		 * Hors France elle n'a pas de sens : on masque la section sur les marches non-FR.
		 * Detection via <html lang="..."> que Shopify pose selon la locale courante. */
		const docLang = (document.documentElement.lang || 'fr').toLowerCase().split('-')[0];
		if (docLang === 'fr') {
			ensureSpace(60);
			section(this._t('quote.section_100_sante', 'Offre 100% Santé'));
			doc.setFont('helvetica', 'italic');
			doc.setFontSize(8);
			doc.setTextColor(110);
			wrappedLine(this._t('quote.sante_intro', 'Offre conforme à la nomenclature LPP 2026 — reste à charge : 0 €.'));
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(9);
			doc.setTextColor(0);
			y += 2;
			tableHeader();

			/* Zone A : prix verre + Rbst SS depuis le lookup LPP (table chargee depuis
			 * lens-lpp-table.json). Si pas de match (sphere non saisie ou lookup echoue),
			 * fallback sur les constantes Px reglementaire generique (32,50 EUR).
			 * Monture : Rbst SS regle = 9 EUR si prix <= 30, 0,05 EUR sinon. Pas de SV4 en
			 * Zone A (les teintes ne sont pas couvertes par l'offre 100% sante). */
			const frameRbstA = this._formatEur(this._getFrameRbst(FRAME_A_PRICE_EUR));
			row4(`${this._t('quote.frame_label', 'Monture')} : ${frameLabelA}`, frameRegrpA, frameRbstA, this._formatEur(FRAME_A_PRICE_EUR));
			for (const eye of eyes) {
				const eyeLabel = eye.eyeKey === 'd'
					? this._t('quote.lens_right', 'Verre droit (OD)')
					: this._t('quote.lens_left', 'Verre gauche (OG)');
				const lppRow = this._lookupLensLpp('A', isProgressif, eye.sphere, eye.cylindre);
				const eyePxA  = lppRow?.px   ?? LENS_A_SINGLE_EUR;
				const eyeRbstA = lppRow?.rbst ?? LENS_A_SINGLE_EUR;
				row4(`${eyeLabel} : ${lensLabelA}`, eye.codes.zoneA, this._formatEur(eyeRbstA), this._formatEur(eyePxA));
				subLineCorrection(eye);
			}
			y += 6;
		}

		/* ============== SECTION ZONE B "Votre devis (option choisie)" ============== */
		ensureSpace(60);
		section(this._t('quote.section_your_devis', 'Votre devis (option choisie)'));
		tableHeader();

		/* Zone B : Rbst SS depuis le lookup LPP (0,05 EUR par defaut dans le CSV pour
		 * tous les verres Classe B). Monture : meme regle que Zone A (9 EUR / 0,05 EUR). */
		if (frameLabel) {
			const frameRbstB = this._formatEur(this._getFrameRbst(framePriceEur));
			row4(`${this._t('quote.frame_label', 'Monture')} : ${frameLabel}`, frameRegrpB, frameRbstB, this._formatEur(framePriceEur));
		}
		for (const eye of eyes) {
			const eyeLabel = eye.eyeKey === 'd'
				? this._t('quote.lens_right', 'Verre droit (OD)')
				: this._t('quote.lens_left', 'Verre gauche (OG)');
			const lppRow = this._lookupLensLpp('B', isProgressif, eye.sphere, eye.cylindre);
			const eyeRbstB = lppRow?.rbst ?? 0.05;
			row4(`${eyeLabel} : ${lensLabelB}`, eye.codes.zoneB, this._formatEur(eyeRbstB), this._formatEur(halfVerre));
			subLineCorrection(eye);
		}
		/* Supplement teinte : 1 ligne par oeil (cf. brief — pas une seule ligne globale).
		 * Pas de prix ni rbst (option intrinseque au verre). */
		if (isSolaire) {
			for (const eye of eyes) {
				const eyeLabel = eye.eyeKey === 'd'
					? this._t('quote.lens_right', 'Verre droit (OD)')
					: this._t('quote.lens_left', 'Verre gauche (OG)');
				row4(`${eyeLabel} : ${sv4Label}`, 'SV4', '', '');
			}
		}

		/* Référence Optimum (italique, info technique) */
		y += 1;
		doc.setFont('helvetica', 'italic');
		doc.setFontSize(8);
		doc.setTextColor(110);
		wrappedLine(`${this._t('quote.label_optimum_ref', 'Référence verre Optimum')} : ${final.optimum_ref || '—'}`);
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(10);
		doc.setTextColor(0);
		y += 4;

		/* Section "Ordonnance" volontairement non rendue dans le PDF (demande Celine 2026-06).
		 * Justification : les mutuelles ne sont pas censees voir les corrections finales.
		 * Les donnees (sphere/cyl/axe/addition + prescripteur) restent utilisees EN INTERNE
		 * pour calculer les codes regroupement LPP / Rbst SS qui apparaissent dans le tableau
		 * "Votre devis", et le nom prescripteur + date + N° SS restent dans le header de
		 * page 1 (colonne droite). On ne supprime QUE l'affichage du bloc detaille. */

		/* ============== SECTION GRAVURE ============== */
		if (this._hasAnyEngravingZone()) {
			ensureSpace(40);
			const eng = this.state.engraving;
			section(this._t('quote.section_engraving', 'Gravure'));
			line(this._t('engraving.color_label', 'Couleur'), this._t(`engraving.color_${eng.color}`, eng.color));
			const zones = ['lt', 'lb', 'rt', 'rb'].filter(k => this._isZoneEngraved(eng.zones[k]));
			for (const k of zones) {
				line(this._t(`engraving.zone_${k}`, k), `« ${eng.zones[k].text} »`);
			}
			const engTotal = this._getEngravingTotal();
			const zoneCount = zones.length;
			const artOpt = this.state.artDetail
				? (this.state.artDetailOptions || []).find(o => o.id === this.state.artDetail)
				: null;
			const pricePerZone = (artOpt && typeof artOpt.price_eur === 'number' && artOpt.price_eur > 0)
				? artOpt.price_eur
				: (zoneCount > 0 ? Math.round(engTotal / zoneCount) : 0);
			line(this._t('quote.label_supplement', 'Supplément'), `${pricePerZone} € × ${zoneCount} = ${engTotal} €`);
			y += 3;
		}

		/* ============== TOTAL ============== */
		ensureSpace(20);
		doc.setDrawColor(0);
		doc.line(marginX, y, pageWidth - marginX, y);
		y += 6;
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(13);
		const totalFrame = (this.state.productPrice || 0) / 100;
		const totalGlobal = totalFrame + totalExtra;
		line(this._t('quote.label_total', 'Total TTC'), `${totalGlobal.toFixed(2)} €`);
		y += 6;

		/* Mentions legales "Il est communique un devis gratuit / Devis valable 2 mois / N° FINESS"
		 * volontairement supprimees du PDF (demande Celine 2026-06). Le N° FINESS reste affiche
		 * dans le header colonne gauche page 1 (via companyInfo + ligne dediee). La validite
		 * "2 mois" est deja exprimee dans le header colonne droite via "Valable jusqu'au DATE". */

		/* ============== CGV (richtext setting) ============== */
		/* Densite resserree (font 7pt, interligne 3.2mm, espace inter-paragraphe 1.2mm)
		 * pour faire tenir les CGV standard (12 articles) sur 1 seule page (demande Celine
		 * 2026-06 : devis = 1 page, CGV = 1 page, total 2 pages). Si Celine ajoute beaucoup
		 * de contenu, ca peut deborder sur p3 — ensureSpace() ajoute alors un saut propre. */
		const legalPlain = this._richtextToPlainText(this.state.devisSettings?.legalText || '');
		if (legalPlain) {
			/* Saut de page force : on veut les CGV systematiquement sur une page dediee
			 * (cf. brief Celine 2026-06 : devis p1 + CGV p2). Sans ce saut, l'espace
			 * libere par la suppression de la section Ordonnance ferait commencer les
			 * CGV en bas de p1, ce qui melangerait le devis et les CGV. */
			doc.addPage();
			y = 20;
			doc.setDrawColor(180);
			doc.line(marginX, y, pageWidth - marginX, y);
			y += 6;
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(10);
			doc.setTextColor(0);
			doc.text(this._t('quote.legal_section_title', 'Conditions générales'), marginX, y);
			y += 5;
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(7);
			doc.setTextColor(80);
			/* Rendu LIGNE PAR LIGNE (et non paragraphe entier).
			 * Sinon, si le richtext de Celine est un seul gros bloc, splitTextToSize
			 * retourne N=70+ lignes, ensureSpace(N * 3.2 + 1.2) voit que ca depasse
			 * 260mm libre -> force addPage AVANT meme le rendu de la 1ere ligne ->
			 * le titre 'Conditions generales' reste seul sur p3 et le contenu va sur p4.
			 * En verifiant ligne par ligne, on remplit la page courante puis on saute. */
			const lineH = 3.2;
			const paraGap = 1.2;
			const paragraphs = legalPlain.split(/\n{2,}/);
			for (const para of paragraphs) {
				if (!para.trim()) continue;
				const wrapped = doc.splitTextToSize(para.trim(), contentWidth);
				for (const line of wrapped) {
					ensureSpace(lineH);
					doc.text(line, marginX, y);
					y += lineH;
				}
				y += paraGap;
			}
			doc.setTextColor(0);
		}

		/* ============== FOOTER (infos societe) sur chaque page ============== */
		this._renderQuotePageFooters(doc, pageWidth, marginX);

		const filenameTpl = this._t('quote.filename', 'devis-{{ ref }}.pdf');
		const filename = filenameTpl.replace('{{ ref }}', ref);
		return { doc, filename, ref, total: totalGlobal };
	},

	/* Helpers PDF — utilises par _generateQuotePdf */

	/* Convertit le HTML richtext Shopify en texte plat utilisable par jsPDF.
	 * Strip tags + decode entities + preserve les sauts de paragraphe. */
	_richtextToPlainText(html) {
		if (!html) return '';
		return String(html)
			.replace(/<\/?(p|div|h[1-6])[^>]*>/gi, '\n\n')
			.replace(/<br\s*\/?>/gi, '\n')
			.replace(/<li[^>]*>/gi, '\n• ')
			.replace(/<\/li>/gi, '')
			.replace(/<[^>]+>/g, '')
			.replace(/&nbsp;/g, ' ')
			.replace(/&amp;/g, '&')
			.replace(/&lt;/g, '<')
			.replace(/&gt;/g, '>')
			.replace(/&#39;/g, "'")
			.replace(/&quot;/g, '"')
			.replace(/[ \t]+\n/g, '\n')
			.replace(/\n{3,}/g, '\n\n')
			.trim();
	},

	/* Dessine le footer "infos societe" sur chaque page du PDF. Appele en fin de
	 * generation (apres tout le contenu, pour que doc.getNumberOfPages() soit final). */
	_renderQuotePageFooters(doc, pageWidth, marginX) {
		const footerPlain = this._richtextToPlainText(this.state.devisSettings?.footerText || '');
		if (!footerPlain) return;
		const pageHeight = doc.internal.pageSize.getHeight();
		const totalPages = doc.getNumberOfPages();
		const contentWidth = pageWidth - 2 * marginX;
		const wrapped = doc.splitTextToSize(footerPlain, contentWidth);
		const footerY = pageHeight - 8 - wrapped.length * 3.5;
		for (let p = 1; p <= totalPages; p++) {
			doc.setPage(p);
			doc.setDrawColor(220);
			doc.line(marginX, footerY - 4, pageWidth - marginX, footerY - 4);
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(7);
			doc.setTextColor(110);
			doc.text(wrapped, pageWidth / 2, footerY, { align: 'center' });
			/* Pagination "Page X / Y" a droite */
			doc.setFontSize(7);
			doc.text(`${p} / ${totalPages}`, pageWidth - marginX, pageHeight - 5, { align: 'right' });
		}
		doc.setTextColor(0);
	},

	/* ====================================================================== */
	/* FOCUS TRAP (copié de mm_cart.js)                                        */
	/* ====================================================================== */

	setupFocusTrap(wrapper, triggerElement) {
		const focusableElements = wrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);
		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		this.focusTrap.handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				this.close();
				this.focusTrap.triggerElement?.focus();
				return;
			}
			if (e.key !== 'Tab') return;
			const focusables = wrapper.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
			);
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);
	},

	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
		}
	},

	/* ====================================================================== */
	/* UTILITIES                                                               */
	/* ====================================================================== */

	_stepTitle(stepKey) {
		if (stepKey === 'correction') return this._t('step.recap.title', 'Choix de la correction');
		if (stepKey === 'combo') return this._t('step.combo.title', 'Personnalisation du verre');
		if (stepKey === 'art_detail') return this._t('art_detail.title', 'Art du détail');
		return this._t(`step.${stepKey}.title`, stepKey);
	},

	_t(key, fallback = '') {
		const i18n = window.MM_LT_I18N || {};
		const value = i18n[key.replace(/\./g, '_')];
		// Shopify renvoie les traductions HTML-safe (apostrophes encodées en &#39;,
		// guillemets en &quot;, etc.). On décode ici pour retourner du texte brut :
		// le caller fera son `_escape()` propre, sinon on double-encoderait.
		return value !== undefined ? this._decodeHtmlEntities(value) : fallback;
	},

	/* Helper i18n pour les labels d'options du tunnel (parcours, type_verre, couleur, ...).
	 * Lookup via _t('options.<id>') avec fallback sur opt.label (FR du JSON tunnel-data).
	 * Si "Translation missing" detecte (Shopify renvoie cette string si la cle n'existe pas
	 * mais qu'on a quand meme une cle), on retombe sur le label FR. */
	_label(opt) {
		if (!opt || !opt.id) return opt?.label || '';
		const translated = this._t('options.' + opt.id, opt.label);
		if (typeof translated === 'string' && translated.indexOf('translation missing') === 0) {
			return opt.label || '';
		}
		return translated || opt.label || '';
	},

	_decodeHtmlEntities(str) {
		if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
		// Utilise le parser HTML natif (textarea) pour décoder toutes les entités
		// nommées et numériques d'un coup.
		const ta = document.createElement('textarea');
		ta.innerHTML = str;
		return ta.value;
	},

	_escape(str) {
		return String(str ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	},

	_announce(msg) {
		if (this.dom.liveRegion) {
			this.dom.liveRegion.textContent = msg;
		}
	},
};

// Auto-init avec vérification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => LensTunnelManager.init());
} else {
	LensTunnelManager.init();
}

// Exposition pour debug et interop éventuelle
window.LensTunnelManager = LensTunnelManager;
